export type Locale = "en" | "es" | "pt";

export const messages = {
  en: {
    languageName: "English",
    nav: {
      features: "Features",
      workflow: "How it works",
      plans: "Plans",
      organizers: "Founding Organizers",
      login: "Log in",
      signup: "Sign up",
    },
    auth: {
      otp: {
        userNotFound: "We couldn't find an account with that email or phone number.",
      },
    },
    adminVenues: {
      back: "Back to My Venues",
      title: "Pending venue verifications",
      subtitle: "Review newly submitted venues before publishing them.",
      loading: "Loading pending venues…",
      empty: "No venues are waiting for verification.",
      countLabel: "Pending venues: {count}",
      refresh: "Refresh list",
      error: "Unable to load pending venues.",
      reviewCta: "Open review",
      hoursHeading: "Hours of operation",
      courtsHeading: "Court breakdown",
      totalCourts: "{count} total courts",
      amenitiesHeading: "Amenities",
      openPlayHeading: "Open play sessions today",
      pendingBadge: "Pending verification",
    },
    adminAccess: {
      back: "Back to dashboard",
      title: "Access & Entitlements",
      subtitle: "Grant user roles, court roles, and subscription access.",
      loading: "Loading access data…",
      error: "Unable to load admin summary.",
      refresh: "Refresh",
      searchPlaceholder: "Search users or courts",
      usersEmpty: "No users match the current filters.",
      courtsEmpty: "No courts match the current filters.",
      adminOnly: "Admins only.",
      unknownCourt: "Unknown court",
      tabs: {
        users: "Users",
        courts: "Courts",
      },
      filters: {
        all: "All",
        roles: "Roles",
        courtRoles: "Court roles",
        subscriptions: "Subscriptions",
      },
      roles: {
        heading: "Roles",
        empty: "No roles yet.",
        add: "Add role",
        select: "Select a role",
      },
      courtRoles: {
        heading: "Court roles",
        empty: "No court roles yet.",
        add: "Add court role",
        update: "Update role",
        selectCourt: "Select a court",
        selectUser: "Select a user",
        roleLabel: "Role",
        searchPlaceholder: "Search courts",
        searchUsersPlaceholder: "Search users",
      },
      access: {
        heading: "Subscription access",
        empty: "No access grants.",
        add: "Add access",
        update: "Update access",
        selectPlan: "Select a plan",
        expiresAt: "Expires at",
      },
      actions: {
        remove: "Remove",
        edit: "Edit",
        close: "Close",
      },
      rules: {
        adminOnly: "Only super admins can add or remove the admin role.",
      },
    },
    adminStats: {
      back: "Back to dashboard",
      title: "App Stats",
      subtitle: "Monitor activity and growth.",
      loading: "Loading stats...",
      error: "Unable to load stats summary.",
      adminOnly: "Admins only.",
      periodLabel: "Period",
      controls: {
        period: "Period",
        timezone: "Timezone",
        topN: "Top N",
        pendingLimit: "Pending limit",
        apply: "Apply",
        periodLabel: "Selected period",
      },
      sections: {
        lifetime: "Lifetime",
        period: "Period",
        activation: "Activation funnel",
        events: "Events",
        engagement: "Engagement",
        geo: "Geo",
        pendingVenues: "Pending venues",
        subscriptions: "Subscriptions",
        subscriptionsPeriod: "Subscriptions (Period)",
        downloadsByOs: "Downloads by OS",
      },
      metrics: {
        uniqueUsers: "Users",
        uniqueDownloads: "Downloads",
        profilesCreated: "Profiles created",
        primaryCourtSelected: "Primary court selected",
        sessionsCount: "Sessions",
        eventsCount: "Events",
        sessionsDuration: "Sessions duration",
        sessionsDurationLifetime: "Lifetime duration",
      },
      empty: {
        downloads: "No downloads.",
      },
      funnel: {
        ofDownloads: "of downloads",
        ofPrevious: "of previous",
      },
    },
    toggles: {
      theme: "Theme",
      language: "Language",
      light: "Light",
      dark: "Dark",
      system: "System",
    },
    hero: {
      eyebrow: "Pickleball players & organizers unite",
      title: "The pickleball app that keeps your courts buzzing",
      description:
        "WhoPlays is the pickleball app built for public courts, clubs, and city programs. Track live court activity, send SMS invites, and grow your pickleball community in one place.",
      primaryCta: "Download the pickleball app",
      primaryHref: "/app-stores",
      secondaryCta: "Become a founding organizer",
      secondaryHref: "/founding-organizers",
      trust: "Trusted by pickleball communities across public and private courts.",
    },
    stats: [
      { value: "Pickleball court feed", label: "See when nearby pickleball courts heat up in real time" },
      { value: "SMS invites", label: "Fill pickleball ladders and drills with one-tap texting" },
      { value: "Pickleball stats", label: "Log DUPR-friendly pickleball matches and track progress" },
    ],
    appShell: {
      tabs: {
        realtime: "Real-Time",
        network: "My Network",
        competitions: "Competitions",
        matches: "Matches",
        rank: "Rank",
        messages: "MSG Center",
      },
      matches: {
        title: "Matches",
        pending: "Pending",
        addMatch: "Add Match",
        loadMore: "Load more",
        loading: "Loading matches...",
        empty: "No matches to show.",
        segments: {
          owned: "Owned",
          mygroup: "My Group",
          mycourts: "My Courts",
        },
        errors: {
          loadFailed: "Unable to load matches right now.",
          saveFailed: "Unable to save the match.",
        },
        editor: {
          title: "New Match",
          subtitle: "Add scores and players.",
          cancel: "Cancel",
          save: "Save",
          saving: "Saving...",
          format: "Format",
          singles: "Singles",
          doubles: "Doubles",
          winner: "Winner",
          selectWinner: "Select winner",
          team1: "Team 1",
          team2: "Team 2",
          player: "Player username",
          score: "Score",
        },
        validation: {
          missingPlayers: "Please add all required players.",
          missingWinner: "Please select a winner.",
        },
      },
      rank: {
        title: "Rank",
        loading: "Loading ranks...",
        empty: "No ranking data to show.",
        segments: {
          wprank: "WP Rank",
          overall: "Overall",
          h2h: "H2H",
        },
        filters: {
          skillAll: "All skills",
          singles: "Singles",
          doubles: "Doubles",
          week: "Week",
          month: "Month",
          all: "All time",
          allCourts: "All courts",
          groupNone: "No group filter",
          groupMyGroup: "My Group",
          groupFollowing: "Following",
          formatAll: "All formats",
          today: "Today",
          yesterday: "Yesterday",
          lastWeek: "Last week",
          lastMonth: "Last month",
          threeMonths: "3 months",
          sixMonths: "6 months",
          year: "1 year",
          fiveYears: "5 years",
          allTime: "All time",
        },
      labels: {
        wins: "Wins",
        losses: "Losses",
        matches: "Matches",
        winPct: "Win%",
        rankLabel: "Rank",
      },
    },
      messagesTab: {
        title: "Message Center",
        empty: "No messages yet.",
        clearAll: "Clear all",
        markAllRead: "Mark all as read",
        delete: "Delete",
        read: "Mark read",
        unread: "Mark unread",
        receivedAt: "Received",
      },
      socket: {
        schedule: "Schedule updated",
        match: "Match update",
        ping: "New ping received",
        dupr: "DUPR rating updated",
        generic: "New notification",
        subscriptionTitle: "Subscription updated",
        subscriptionBody: "Your subscription status has been refreshed.",
        matchUnanimousTitle: "Match unanimously accepted",
        matchUnanimousBody: "Fantastic news! A match you played has been officially accepted by all players. It's now official!",
        matchAcceptedTitle: "Player accepted match",
        matchAcceptedBody: "{player} has accepted the match! Waiting for other players to confirm.",
        matchPendingTitle: "Pending matches",
        matchPendingBody: "New pending match(es) result! Confirm and make it official. Check now to contribute to your ranking.",
        duprUpdatedTitle: "DUPR rating updated",
        duprUpdatedBody: "Singles: {singles}\nDoubles: {doubles}",
        questionTitle: "New schedule question",
      },
      subscriptions: {
        title: "Subscriptions",
        subtitle: "Choose a plan to unlock scheduling power with WhoPlays.",
        detailsLink: "View full plan details",
        currentPlan: "Current plan",
        statusLabel: "Status",
        free: "Free",
        active: "Active",
        select: "Select",
        checkout: "Checkout with Stripe",
        close: "Close",
        manage: "Manage / cancel in Stripe",
        manageHint: "We'll open Stripe's customer portal. If you don't see your subscription, use your receipt email.",
        providerManaged: "Managed by {provider}",
        providerManagedHint: "To manage or cancel this subscription, open {provider}.",
        error: "We couldn't start checkout. Please try again in a moment.",
        successTitle: "Checkout completed",
        successToast: "Subscription checkout finished. Refreshing your access…",
        cancelTitle: "Checkout canceled",
        cancelToast: "You canceled Stripe checkout.",
        plans: {
          "com.whoplays.scheduling.basic.monthly": {
            label: "Scheduler Basic (Monthly)",
            price: "Monthly billing",
          },
          "com.whoplays.scheduling.basic.yearly": {
            label: "Scheduler Basic (Yearly)",
            price: "Yearly billing",
          },
          "com.whoplays.scheduling.pro.monthly": {
            label: "Scheduler Pro (Monthly)",
            price: "Monthly billing",
          },
          "com.whoplays.scheduling.pro.yearly": {
            label: "Scheduler Pro (Yearly)",
            price: "Yearly billing",
          },
        },
      },
      subscriptionLimits: {
        title: "Limit reached",
        sms: "You've reached your monthly limit of {limit} SMS invites.",
        players: "You've reached the {limit}-player invite limit per schedule.",
        events: "You've hit your limit of {limit} active schedules with invites.",
        generic: "You've reached your current limit of {limit}.",
        upgrade: "Upgrade now",
        cancel: "Cancel",
      },
      sidebar: {
        welcome: "Welcome back",
        kpiWins: "Wins",
        kpiLosses: "Losses",
        kpiPending: "Pending",
        quickActions: {
          language: "Change language",
          languageNext: "Next: {language}",
          theme: "Toggle theme",
          tips: "Tips (coming soon)",
          tipsSoon: "Tips are coming soon.",
          logout: "Log out",
        },
        toggles: {
          dnd: "Do Not Disturb",
          tips: "Tips",
          share: "Share",
          language: "Language",
        },
        actions: {
          profile: "Profile",
          preferredCourts: "My Venues",
          schedule: "My Play Schedule",
          linkDupr: "Link with DUPR®",
          subscriptions: "Subscriptions",
          invite: "Invite Players",
          privacy: "Privacy Policy",
          settings: "Settings & Preferences",
          appStats: "App Stats",
          logout: "Log out",
        },
        admin: {
          label: "Admin",
          access: "Access & Entitlements",
        },
        chartLabel: "Win / Loss ratio",
        chartWins: "Wins",
        chartLosses: "Losses",
        chartPending: "Pending",
        chartNoData: "No match history yet.",
        chartWinRate: "Win rate",
        chartTotal: "Total",
        skillLevels: {
          beginner: "Beginner",
          intermediate: "Intermediate",
          advanced: "Advanced",
        },
        rankings: {
          wpRankLabel: "WP Rank",
          duprLabel: "DUPR",
          singlesShort: "S",
          doublesShort: "D",
          notRated: "NR",
        },
      },
      header: {
        subtitleRank: "Rank · Global",
        moreLabel: "Menu",
        currentLabel: "Current",
      },
      analytics: {
        tabSwitch: "tab_switch",
        sidebarOpen: "sidebar_open",
        sidebarClose: "sidebar_close",
      },
      dupr: {
        title: "Link with DUPR®",
        subtitle: "Sign in to DUPR to sync your ratings.",
        instructions: "Complete the DUPR sign-in below. We never store your password.",
        waiting: "Waiting for DUPR sign-in...",
        processing: "Linking your DUPR account...",
        success: "DUPR account linked successfully.",
        error: "Could not link your DUPR account right now.",
        originError: "Ignored message from an untrusted source.",
        missingConfig: "DUPR integration is not configured.",
        close: "Close",
        unauthorized: "You need to be signed in to link DUPR. Please log in and try again.",
      },
      realTime: {
        ribbonLabel: "Your courts",
        refresh: "Refresh",
        noCourtsTitle: "Add a preferred court to see live activity.",
        courtSelectorLabel: "Select a court",
        summary: {
          title: "Players at {court}",
          allLevels: "All levels",
          advanced: "Advanced",
          intermediate: "Intermediate",
          beginner: "Beginner",
          metrics: {
            willing: "Willing to play",
            privateGroups: "Private groups",
            following: "Following up",
          },
          metricsShort: {
            willing: "Willing",
            privateGroups: "Private",
            following: "Following",
          },
          updated: "Updated {time}",
        },
        whosNextTitle: "Who's Next",
        myScheduleTitle: "My Schedule",
        myQuestionsTitle: "My Questions",
        day: {
          today: "Today",
          tomorrow: "Tomorrow",
        },
        metrics: {
          openPlay: "Open Play",
          event: "Event",
          privateGroup: "Private Group",
        },
        carousels: {
          noData: "No activity scheduled yet.",
          dotsLabel: "Jump to card {index}",
        },
        detail: {
          noActivity: "No upcoming activity for this play type.",
          close: "Close",
          profileTitle: "Player profile",
          openProfile: "Open full profile",
          unknownPlayer: "Unknown player",
          status: {
            host: "Host",
            accepted: "Accepted",
            pending: "Pending",
            declined: "Declined",
          },
        },
        schedule: {
          empty: "No upcoming matches yet.",
          badges: {
            invited: "Invited",
            hosted: "Hosted by {name} at {court}",
            hostedSelf: "Hosted by me at {court}",
          },
          cardTime: "My Sched: {time}",
          counts: {
            invited: "Invited",
            confirmed: "Confirmed",
          },
          confirmedLine: "Confirmed: {confirmed}",
          confirmedLineWithLimit: "Confirmed: {confirmed} of {limit}",
        },
        questions: {
          empty: "No questions for this court.",
          status: {
            pending: "Pending",
            answered: "Answered",
          },
        },
        emptyHero: {
          title: "Schedule a match and invite players!",
          bullets: [
            "See who's coming — live!",
            "Invite players via SMS & track responses",
            "Keep everyone updated via SMS or in-app",
          ],
          cta: "Schedule an Event",
        },
        fabLabel: "Schedule Event",
      },
      network: {
        title: "My Network",
        subtitle: "Find and manage players you follow.",
        searchPlaceholder: "Search players",
        search: "Search",
        segments: {
          suggestions: "Suggestions",
          following: "Following",
          followers: "Followers",
        },
        courtFilter: "Courts",
        allCourts: "All courts",
        loading: "Loading players…",
        error: "Unable to load players right now.",
        empty: "No players to show.",
        unknownSkill: "Skill unknown",
        follow: "Follow",
        following: "Following",
        refresh: "Refresh",
        loadMore: "Load more",
        actionError: "Could not update follow. Try again.",
        levelCompetition: {
          recreational: "Recreational",
          competitive: "Competitive",
        },
        followTitle: "Follow {name}",
        followingTitle: "Following {name}",
        close: "Close",
        toggleGroup: "Add to My Group stats",
        toggleNotify: "Notify me about this player",
        unfollow: "Unfollow",
        save: "Save",
      },
    },
    profileEdit: {
      loading: "Loading profile…",
      header: {
        title: "Edit Profile",
        back: "Back",
        cancel: "Cancel",
        save: "Save Preferences",
        saving: "Saving…",
      },
      sections: {
        avatar: {
          title: "Avatar",
          subtitle: "Upload a clear photo so players recognize you.",
          button: "Change photo",
          uploading: "Uploading…",
          helper: "JPG or PNG up to 5MB.",
        },
        personal: { title: "Personal info" },
        location: { title: "Location" },
        preferences: { title: "Preferences & skill" },
        bio: { title: "Bio", placeholder: "Share a short introduction" },
      },
      fields: {
        name: "Full name",
        nickname: "Nickname",
        email: "Email",
        phone: "Phone number",
        country: "Country",
        state: "State",
        city: "City",
        zipcode: "Zip code",
        status: "Status",
        preferredLevel: "Preferred level",
        matchType: "Match type",
        skill: "Skill level",
        selfRanking: "Self ranking",
        competition: "Competition level",
        defaultAudience: "Default audience",
        followerPrivacy: "Follower privacy",
      },
      helpers: {
        emailOptional: "Email is optional and can be used for notifications.",
        emailLogin: "Changing your email may require verification.",
        phoneLocked: "This phone number is your login and cannot be changed.",
        phoneFormat: "Use international format, e.g. +1…",
      },
      success: {
        saved: "Profile saved.",
        photo: "Profile photo updated.",
        email: "Email verified.",
        phone: "Phone verified.",
      },
      errors: {
        name: "Name is required.",
        email: "Enter a valid email address.",
        phone: "Enter a valid phone number in international format.",
        code: "Enter the 6-digit code.",
        save: "Unable to save profile.",
        upload: "Unable to upload avatar.",
        verificationStart: "Unable to start verification.",
        verificationConfirm: "Unable to verify code.",
      },
      verification: {
        verifyEmail: "Verify email",
        reverifyEmail: "Re-verify email",
        verifyPhone: "Verify phone",
        reverifyPhone: "Re-verify phone",
        badge: "Verified",
        modalTitle: "Enter verification code",
        modalDescriptionEmail: "We sent a 6-digit code to {target}. Enter it below to verify your email.",
        modalDescriptionPhone: "We sent a 6-digit code to {target}. Enter it below to verify your phone.",
        submit: "Verify",
        submitting: "Verifying…",
        cancel: "Cancel",
      },
      options: {
        competition: [
          { value: 1, label: "Recreational" },
          { value: 2, label: "Competitive" },
        ],
      },
      legal: "WhoPlays US · Profile settings",
    },
    myVenues: {
      back: "Back to app",
      title: "My Venues",
      subtitle: "Choose your home court and keep a shortlist of places you love to play.",
      loading: "Loading venues…",
      primary: {
        heading: "Primary venue",
        empty: "You haven’t selected a primary venue yet.",
        required: "Pick a primary venue to personalize your experience.",
        clear: "Clear primary venue",
      },
      preferred: {
        heading: "Preferred venues",
        empty: "Add venues you play at regularly to keep them handy.",
        remove: "Remove",
        makePrimary: "Set as primary",
        add: "Add to preferred",
        countLabel: "selected",
      },
      badges: {
        primary: "Primary",
        preferred: "Preferred",
      },
      search: {
        heading: "Find venues",
        placeholder: "Search by venue name or address",
        action: "Search",
        clear: "Clear search",
        useLocation: "Use my location",
        locating: "Locating…",
        locationReady: "Location ready",
        locationError: "We couldn’t get your location.",
        prompt: "Search for a venue or share your location to see nearby options.",
        noResults: "No venues matched your search. Try different keywords.",
        loadMore: "Load more results",
        loading: "Loading venues…",
        setPrimary: "Set as primary",
        addPreferred: "Add to preferred",
        activeLabel: "Results for “{query}”",
        error: "We couldn’t search for venues right now.",
        viewDetails: "View details",
        hideDetails: "Hide details",
      },
      actions: {
        save: "Save venues",
        saving: "Saving venues…",
        saved: "Venues updated successfully.",
        error: "Unable to save venues.",
        suggest: "Suggest a venue",
        reviewPending: "Review pending venues",
        warning: "Venues saved, but we couldn’t refresh the latest details.",
      },
    },
    suggestVenue: {
      backToVenues: "Back to My Venues",
      title: "Suggest a Venue",
      subtitle: "Share details about a new location so we can review it for the community.",
      steps: {
        basic: "Basics",
        details: "Details",
        openPlay: "Open Play",
        photos: "Photos",
        contact: "Contact",
        review: "Review",
      },
      actions: {
        previous: "Back",
        next: "Next",
        submit: "Submit suggestion",
        submitting: "Submitting…",
        approve: "Approve & publish",
        markPending: "Return for updates",
        delete: "Delete venue",
      },
      loadingExisting: "Loading existing venue details…",
      successMessage: "Thanks! We received your venue suggestion.",
      standaloneSuccess: {
        title: "Thanks for submitting a new venue!",
        body: "Our review team is validating the details. Venues typically go live within 24–72 hours once everything checks out.",
        helper: "We’ll reach out if we need more information.",
        ctaPrimary: "Suggest another venue",
        ctaSecondary: "Close",
      },
      successApproved: "Venue approved and published.",
      successPending: "Marked as pending updates.",
      successDeleted: "Venue deleted.",
      errors: {
        basicRequired: "Enter the name and full address before continuing.",
        detailsRequired: "Add at least one court configuration and pick a surface.",
        descriptionRequired: "Describe the venue so players know what to expect.",
        contactRequired: "Provide your contact details so we can follow up if needed.",
        confirmInformation: "Please confirm that the information you provided is accurate.",
        submitFailed: "We couldn’t save your suggestion right now. Try again shortly.",
        loadVenue: "We couldn’t load that venue. Try again shortly.",
        deleteFailed: "We couldn’t delete that venue. Try again shortly.",
      },
      confirmDelete: "Delete this venue? This action cannot be undone.",
      basic: {
        heading: "Basic information",
        subheading: "Start with name and address so we can locate the venue.",
        nameLabel: "Venue name",
        namePlaceholder: "Riverside Pickleball Complex",
        shortNameLabel: "Short name",
        shortNamePlaceholder: "Optional short label",
        addressLabel: "Street address",
        addressPlaceholder: "123 Main Street",
        cityLabel: "City",
        stateLabel: "State / Region",
        zipLabel: "Postal code",
        countryLabel: "Country",
        countryPlaceholder: "USA",
        latitudeLabel: "Latitude",
        longitudeLabel: "Longitude",
        mapHeading: "Map preview",
        mapHelper: "Set latitude and longitude to preview the location.",
        mapMissing: "Add coordinates to preview the map.",
        mapTitle: "Map preview for {name}",
        mapDefaultName: "this venue",
        mapLoadError: "We couldn’t load the map. Check your network and try again.",
        mapSearchingForward: "Looking up the location for this address…",
        mapSearchingReverse: "Looking up the address for these coordinates…",
        mapGeocodeError: "We couldn’t find coordinates for that address. Try refining it.",
        mapPendingHeading: "Update the address with this location?",
        mapPendingApply: "Use this address",
        mapPendingIgnore: "Keep current address",
      },
      details: {
        heading: "Venue details",
        subheading: "Tell us about the courts, surfaces, and amenities.",
        surfaceLabel: "Primary surface",
        surfacePlaceholder: "Select surface",
        typeLabel: "Venue type",
        typePublic: "Public (open to everyone)",
        typePrivate: "Private (membership required)",
        courtHeading: "Court breakdown",
        courtAdd: "Add court row",
        courtTypeLabel: "Court type",
        courtTypePlaceholder: "Choose type",
        courtTypeDedicated: "Dedicated",
        courtTypeShared: "Shared Net",
        courtTypeLined: "Lined Only",
        courtEnvironmentLabel: "Environment",
        courtEnvironmentPlaceholder: "Choose environment",
        environmentIndoor: "Indoor",
        environmentOutdoor: "Outdoor",
        environmentCovered: "Covered",
        courtQuantityLabel: "Quantity",
        courtRemove: "Remove row",
        amenitiesHeading: "Amenities",
        amenitiesOptions: [
          "Lighting",
          "Restrooms",
          "Water Stations",
          "Seating",
          "Pro Shop",
          "Lockers",
          "Showers",
          "Food/Drinks",
          "Parking",
          "Wheelchair Accessible",
          "Shade Structures",
          "Ball Machine",
          "Lessons Available",
        ],
        hoursHeading: "Hours of operation",
        timeSlots: [
          "5:00 AM",
          "6:00 AM",
          "7:00 AM",
          "8:00 AM",
          "9:00 AM",
          "10:00 AM",
          "11:00 AM",
          "12:00 PM",
          "1:00 PM",
          "2:00 PM",
          "3:00 PM",
          "4:00 PM",
          "5:00 PM",
          "6:00 PM",
          "7:00 PM",
          "8:00 PM",
          "9:00 PM",
          "10:00 PM",
          "11:00 PM",
          "12:00 AM",
        ],
        until: "to",
        closedLabel: "Closed",
        descriptionLabel: "Description",
        descriptionPlaceholder: "Highlight features, court quality, and anything players should know.",
        phoneLabel: "Phone",
        phonePlaceholder: "(Optional) Contact number",
        websiteLabel: "Website",
        websitePlaceholder: "https://…",
      },
      openPlay: {
        heading: "Open play schedule",
        subheading: "Add drop-in sessions so players know when to show up.",
        sessionsCount: "{count} sessions",
        copyWeekdays: "Copy to weekdays",
        copyWeekend: "Copy to weekend",
        addSession: "Add session",
        emptyLabel: "No open play added for this day yet.",
        startLabel: "Start",
        endLabel: "End",
        levelLabel: "Skill level",
        statusLabel: "Status",
        removeSession: "Remove session",
        timeSlots: [
          "6:00 AM",
          "7:00 AM",
          "8:00 AM",
          "9:00 AM",
          "10:00 AM",
          "11:00 AM",
          "12:00 PM",
          "1:00 PM",
          "2:00 PM",
          "3:00 PM",
          "4:00 PM",
          "5:00 PM",
          "6:00 PM",
          "7:00 PM",
          "8:00 PM",
          "9:00 PM",
          "10:00 PM",
        ],
        levelOptions: ["All Levels", "Beginner", "Beginner+", "Intermediate", "Intermediate+", "Advanced", "Tournament Players"],
        statusOptions: ["Available", "Few Spots", "Full", "Members Only", "Open to Public", "Registration Required"],
      },
      photos: {
        heading: "Venue photos",
        subheading: "Upload a few images so players can see the courts.",
        helper: "Drag and drop images or browse files from your device.",
        uploadCta: "Select photos",
        supported: "Use JPG or PNG files (max 5 MB each).",
        current: "Existing photos",
        pending: "Photos to upload",
        remove: "Remove",
      },
      contact: {
        heading: "Contact information",
        subheading: "Tell us who we can reach out to if we need more details.",
        nameLabel: "Your name",
        emailLabel: "Email",
        phoneLabel: "Phone number",
        roleLabel: "Your role",
        rolePlaceholder: "Select role",
        roleOwner: "Owner / Operator",
        roleManager: "Manager",
        roleEmployee: "Employee",
        rolePlayer: "Regular player",
        roleVisitor: "Visitor",
        roleOther: "Other",
        notesLabel: "Additional notes",
        notesPlaceholder: "Any helpful context for our review team.",
        confirmationLabel: "I confirm the information is accurate and follows the community guidelines.",
      },
      review: {
        heading: "Review & submit",
        subheading: "Look everything over before sending it to our team.",
        basic: "Basic information",
        name: "Venue name",
        address: "Address",
        type: "Venue type",
        surface: "Surface",
        courts: "Courts",
        amenities: "Amenities",
        description: "Description",
        photos: "Photos",
        contact: "Contact details",
        contactName: "Name",
        contactEmail: "Email",
        contactPhone: "Phone",
        contactRole: "Role",
        notes: "Notes",
        unknown: "Unknown",
      },
      duplicates: {
        title: "This venue may already exist",
        description: "We found similar venues. If one matches, open it instead of creating a duplicate.",
        view: "View venue",
        continue: "Continue anyway",
        cancel: "Go back",
      },
    },
    settings: {
      header: {
        title: "Settings & Preferences",
        description: "Control appearance, privacy, and notifications across WhoPlays.",
        back: "Back",
        cancel: "Cancel",
        save: "Save Preferences",
        saving: "Saving…",
      },
      guard: {
        title: "Settings unavailable",
        description: "Complete your profile to unlock settings and preferences.",
        cta: "Edit profile",
      },
      theme: {
        title: "Theme preference",
        description: "Choose how WhoPlays looks on this device.",
        options: {
          system: { label: "System", description: "Match your device setting" },
          light: { label: "Light", description: "Bright interface" },
          dark: { label: "Dark", description: "Low-light friendly" },
        },
      },
      language: {
        title: "Language",
        description: "Switch the interface language instantly.",
      },
      availability: {
        title: "Availability",
        options: {
          1: { title: "Available", description: "Players can invite you to matches." },
          2: { title: "Unavailable", description: "Appear offline to everyone." },
          6: { title: "Private group", description: "Visible only to members of private groups." },
        },
      },
      visibility: {
        title: "Profile visibility",
        options: {
          1: { title: "Public", description: "Anyone can view your profile." },
          3: { title: "Registered players", description: "Only WhoPlays members can view your profile." },
          4: { title: "Connections", description: "Only your connections can view full details." },
          5: { title: "Private", description: "Only you and admins can view your profile." },
        },
      },
      followers: {
        title: "Follower visibility",
        options: {
          1: { title: "Public", description: "Followers see your public activity." },
          2: { title: "Primary court", description: "Limit updates to your primary court community." },
          3: { title: "All courts", description: "Followers see updates from all courts you follow." },
          4: { title: "Friends", description: "Only approved friends see your updates." },
        },
      },
      notifications: {
        title: "Notifications",
        description: "Choose how you want to hear from WhoPlays.",
        items: {
          tournamentReminders: { title: "Tournament reminders", description: "Get notified before your registered tournaments start." },
          matchNotifications: { title: "Match updates", description: "Receive alerts when matches are scheduled or updated." },
          partnerRequests: { title: "Partner requests", description: "Know when players want to connect." },
          communityEvents: { title: "Community events", description: "Stay in the loop on events near you." },
          emailNotifications: { title: "Email notifications", description: "Receive updates via email." },
          eventInvitesSms: { title: "Event invites via SMS", description: "Get text invites you can reply to." },
          matchInvitesSms: { title: "Match alerts via SMS", description: "Receive SMS reminders for matches." },
          scoringUpdatesSms: { title: "Score by text", description: "Confirm results directly via SMS." },
        },
      },
      account: {
        title: "Account shortcuts",
        links: {
          connectedAccounts: { title: "Connected accounts", description: "Manage linked social accounts." },
          dataPrivacy: { title: "Data & privacy", description: "Review how We handle your data." },
          deleteAccount: { title: "Delete account", description: "Permanently remove your account." },
        },
      },
      messages: {
        saved: "Preferences saved successfully.",
        error: "We couldn't save your preferences. Please try again.",
      },
    },
    homeVenues: {
      title: "Courts near you",
      description:
        "Search WhoPlays venues by name or address and keep tabs on open play sessions happening today.",
      pageTitle: "Pickleball venues directory",
      pageDescription:
        "Search by name or address, filter by amenities, and explore open play schedules near you.",
      useMyLocation: "Use my location",
      locating: "Detecting location...",
      searchPlaceholder: "Search by venue name or address",
      searchAriaLabel: "Search venues",
      searchCta: "Search",
      promptLocation: "Share your location or search to discover nearby venues.",
      clearFilters: "Clear filters",
      errorLoading: "We couldn't load nearby venues. Please try again.",
      noResults: "No venues match your filters yet.",
      loading: "Loading venues...",
      loadMore: "Load more",
      loadingMore: "Loading more venues...",
      distanceAway: "{distance} away",
      verifiedBadge: "Verified",
      courtsHeading: "Court breakdown",
      courtsHeadingWithTotal: "Court breakdown · {count} courts",
      courtsFallback: "{count} total courts",
      surfaceLabel: "Primary surface: {surface}",
      totalCourts: "{count} total courts",
      amenitiesHeading: "Amenities",
      openPlayHeading: "Today's open play",
      hoursHeading: "Hours of operation",
      hoursClosed: "Closed",
      contactPhone: "Call {phone}",
      visitWebsite: "Visit website",
      viewOnMap: "View on map",
      viewAllVenues: "Explore all venues",
      viewDetails: "View details",
      backToHome: "Back to home",
      openStatusNow: "Open now",
      closedStatusNow: "Closed now",
      closedStatusToday: "Closed today",
      openUntil: "Open until {time}",
      opensAt: "Opens at {time}",
      closedForDay: "Closed for the day",
      statusUnknown: "Status unavailable",
      statusClosedTemporary: "Temporarily closed",
      statusClosedPermanent: "Permanently closed",
      statusClosedOutOfSeason: "Closed for the season",
      publicVenue: "Public",
      privateVenue: "Private",
      mapFallbackTitle: "Map for {name}",
      mapFallbackNoLocation: "Location details unavailable.",
    },
    features: {
      title: "Everything you need to keep games flowing",
      description:
        "Follow courts, send invites, and keep play history organized with tools made for public-court pickleball.",
      cards: [
        {
          title: "Live court awareness",
          description:
            "Check crowd levels before you leave home and share quick snapshots with everyone who follows the court.",
          points: [
            "Follow public courts and see who’s playing by skill",
            "Browse upcoming sessions and pickup games in one feed",
            "Post quick updates that notify followers instantly",
          ],
        },
        {
          title: "One-tap sessions & SMS invites",
          description:
            "Create a ladder, drill, or casual hit in under a minute and let players RSVP by text—no app required.",
          points: [
            "Flexible templates for open play, ladders, or private runs",
            "Invite anyone by SMS and track responses in real time",
            "Keep the whole crew in the loop with automatic reminders",
          ],
        },
        {
          title: "Player profiles & stats",
          description:
            "Track your history, sync DUPR results, and keep notes on partners and rivals so every match gets better.",
          points: [
            "Record scores, verified matches, and personal notes",
            "Self-rate or connect to DUPR to follow rating changes",
            "Follow players, start chats, and build your crew",
          ],
        },
      ],
    },
    social: {
      title: "Follow the WhoPlays pickleball community",
      description:
        "Tips, product drops, and behind-the-scenes footage from the players building the WhoPlays pickleball app.",
      links: [
        { name: "Instagram", handle: "@whoplaysus", href: "https://www.instagram.com/whoplaysus/" },
        { name: "Facebook", handle: "WhoPlays Pickleball", href: "https://www.facebook.com/profile.php?id=61550796384597" },
        { name: "YouTube", handle: "WhoPlays Pickleball", href: "https://www.youtube.com/channel/UC9JuYJ_QR3ykzjXgnKIRviA" },
        { name: "LinkedIn", handle: "WhoPlays US", href: "https://www.linkedin.com/company/whoplays-us-inc/?viewAsMember=true" },
        { name: "X", handle: "@WhoPlaysUS", href: "https://x.com/WhoPlaysUS" },
        { name: "TikTok", handle: "@whoplays05", href: "https://tiktok.com/@whoplays05" },
      ],
    },
    workflow: {
      title: "Meet up fast in four steps",
      description:
        "Find a crowd, schedule play, confirm by text, and keep your stats. WhoPlays handles the coordination so you can stay on court.",
      steps: [
        {
          title: "Discover",
          description:
            "Open the court feed to see who’s already out there and what skill mix is on the courts.",
        },
        {
          title: "Create",
          description:
            "Start a game, ladder, or drill session in seconds with friendly defaults made for public courts.",
        },
        {
          title: "Invite",
          description:
            "Send SMS invites or share the link—players reply with Yes/No and WhoPlays updates the roster instantly.",
        },
        {
          title: "Record",
          description:
            "Log scores, sync to DUPR if you want, and watch your personal history grow over time.",
        },
      ],
    },
    testimonials: {
      title: "Organizers who switched to WhoPlays",
      items: [
        {
          quote:
            "“The court feed alone changed how we plan. I can check who’s already confirmed and text the rest without leaving the app.”",
          name: "Lauren Mitchell",
          role: "Director, Rally Point Pickleball",
        },
        {
          quote:
            "“SMS RSVPs are a game changer. Even players who hate apps stay in the loop and ladders stay full.”",
          name: "Brian Kim",
          role: "Head Pro, Marina Courts",
        },
      ],
    },
    finalCta: {
      title: "Help shape the next release of WhoPlays",
      description:
        "Join the founding organizer group to test new pickleball scheduling features, request integrations, and get direct support from the team.",
      primaryCta: "Become a founding organizer",
      primaryHref: "/founding-organizers",
      secondaryCta: "Download the pickleball app",
      secondaryHref: "/app-stores",
    },
    footer: {
      tagline: "Built in the US for the global pickleball community.",
      rights: "All rights reserved.",
      links: {
        privacy: "Privacy Policy",
        plans: "Plans",
        accountDeletion: "Account deletion",
      },
    },
    subscriptionPlans: {
      hero: {
        eyebrow: "Flexible pricing",
        title: "Plans built for every organizer",
        description:
          "Start free and scale into pro-grade automations the moment your programs grow. Switch tiers at any time.",
        note: "All plans include event reminders, schedule insights, and attendance tracking.",
      },
      cards: [
        {
          id: "free",
          name: "Free",
          badge: "Start here",
          priceMonthly: "Free / month",
          priceYearly: "Free / year",
          description: "Perfect for testing core scheduling flows with small rosters and manual confirmations.",
          features: [
            "Unlimited schedules without invitations",
            "Invite up to 5 players per event",
            "15 SMS invitations every month",
            "Schedule insights & notifications",
          ],
          ctaLabel: "Create free account",
        },
        {
          id: "basic",
          name: "Scheduling Basic",
          badge: "Popular",
          priceMonthly: "$2.99 / month",
          priceYearly: "$24.99 / year",
          description: "Handle weekly drop-ins with larger invitation lists and automated SMS follow-ups.",
          features: [
            "Up to 5 active invitation-enabled schedules",
            "Invite up to 20 players per event",
            "75 SMS invitations every month",
            "Ad-free, branded organizer experience",
          ],
          ctaLabel: "Choose Basic",
          highlighted: true,
        },
        {
          id: "pro",
          name: "Scheduling Pro",
          badge: "Best value",
          priceMonthly: "$4.99 / month",
          priceYearly: "$39.99 / year",
          description: "Unlock unlimited invitations, priority SMS delivery, and scale programs without limits.",
          features: [
            "Unlimited invitation-enabled schedules",
            "Unlimited players per event",
            "150 SMS invitations every month",
            "Priority SMS delivery for faster confirmations",
          ],
          ctaLabel: "Upgrade to Pro",
        },
      ],
      table: {
        title: "Compare what's included",
        description:
          "Every tier keeps core scheduling tools unlocked. Upgrade when you need more invitations or faster confirmations.",
        featureColumnLabel: "Feature",
        planLabels: {
          free: "Free",
          basic: "Scheduling Basic",
          pro: "Scheduling Pro",
        },
        rows: [
          {
            label: "Max active schedules without invitations",
            values: { free: "Unlimited", basic: "Unlimited", pro: "Unlimited" },
          },
          {
            label: "Max active schedules with invitations",
            values: { free: "1", basic: "5", pro: "Unlimited" },
          },
          {
            label: "Max players invited per schedule",
            values: { free: "5", basic: "20", pro: "Unlimited" },
          },
          {
            label: "Max courts per event",
            values: { free: "1", basic: "1", pro: "1" },
          },
          {
            label: "Max SMS invitations per month",
            values: { free: "15", basic: "75", pro: "150" },
          },
          {
            label: "Schedule insights (who’s joining?)",
            values: { free: "check", basic: "check", pro: "check" },
          },
          {
            label: "Event reminders & notifications",
            values: { free: "check", basic: "check", pro: "check" },
          },
          {
            label: "Ad-free experience",
            values: { free: "cross", basic: "check", pro: "check" },
          },
          {
            label: "Scheduling window (days in advance)",
            values: { free: "30", basic: "30", pro: "30" },
          },
          {
            label: "Organizer can be outside the roster",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Max round robin events per month",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Max players per round robin",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Max single & double elimination tournaments per month",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Max players per single & double elimination tournament",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Priority SMS delivery",
            values: { free: "cross", basic: "check", pro: "check" },
          },
          {
            label: "Monthly price",
            values: { free: "Free", basic: "$2.99", pro: "$4.99" },
          },
          {
            label: "Yearly price",
            values: { free: "Free", basic: "$24.99", pro: "$39.99" },
          },
        ],
        legend: {
          check: "Included",
          cross: "Not included",
        },
      },
      cta: {
        title: "Have questions about team access or tournaments?",
        description:
          "We’re adding competitive formats soon. Join the founding organizer program to shape the roadmap and secure early pricing.",
        primaryLabel: "Talk with us",
        primaryHref: "/founding-organizers",
        secondaryLabel: "Explore product updates",
        secondaryHref: "/bugtracker",
      },
      footnote: "Manage upgrades directly inside the WhoPlays mobile app. Pricing shown in USD.",
    },
    foundingOrganizers: {
      hero: {
        eyebrow: "Founding organizer program",
        title: "Run your pickleball league smarter — and free for life",
        highlight: "The first 100 organizers get lifetime access to WhoPlays US.",
        description:
          "Host events, automate confirmations, and shape upcoming features. Keep your groups active and we’ll lock in Scheduler Pro for life when subscriptions launch.",
        badges: [
          {
            id: "google-play",
            label: "Get it on Google Play",
            href: "https://play.google.com/store/apps/details?id=com.whosplays.app&pli=1",
            image: "/images/google-play-badge.png",
            width: 500,
            height: 120,
            className: "h-18 w-auto",
          },
          {
            id: "app-store",
            label: "Download on the App Store",
            href: "https://apps.apple.com/app/apple-store/id6451442623?pt=126911496&ct=Founding%20Organizers&mt=8",
            image: "/images/apple-store-badge.svg",
            width: 175,
            height: 60,
            className: "h-12 w-auto",
          },
        ],
        primaryCtaLabel: "Schedule your first event now",
          primaryCtaHref: "/app-stores",
      },
      lifetime: {
        title: "Lifetime Scheduler Pro for active organizers",
        description:
          "Once the subscription feature is live, we’ll automatically grant lifetime Pro access to organizers who stay engaged.",
        bullets: [
          "Use WhoPlays to schedule and host your sessions.",
          "Stay active with at least one event every 60 days.",
          "Keep your community growing and we’ll keep Pro unlocked.",
        ],
        footnote: "This reward is our thank-you to the leaders who help us build the future of pickleball scheduling.",
      },
      benefits: {
        title: "Key benefits",
        items: [
          "Schedule games in seconds",
          "Invite players by SMS — no app required",
          "Limit RSVPs and track confirmations",
          "See who’s playing by level and time",
          "Manage your events like a pro",
        ],
      },
      reminder: {
        headline: "Only 100 organizers get free lifetime access",
        subheadline: "Claim your spot before it’s gone.",
      },
      video: {
        title: "See WhoPlays scheduling in action",
        src: "https://whoplays.us/wp-content/uploads/2025/03/WhoPlays-Scheduler-Web-Version.mp4",
        poster: "https://whoplays.us/wp-content/uploads/2025/03/Image.png",
      },
    },
    privacyPolicy: {
      title: "Privacy Policy",
      lastUpdated: "Last updated: March 30, 2025",
      intro: [
        "This Privacy Policy describes our policies and procedures on the collection, use, and disclosure of your information when you use the Service and explains your privacy rights and how the law protects you.",
        "We use your personal data to provide and improve the Service. By using the Service, you agree to the collection and use of information in accordance with this Privacy Policy.",
      ],
      sections: [
        {
          heading: "Interpretation and Definitions",
          subSections: [
            {
              subheading: "Interpretation",
              paragraphs: [
                "The words with capitalized initials have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.",
              ],
            },
            {
              subheading: "Definitions",
              paragraphs: ["For the purposes of this Privacy Policy:"],
              definitions: [
                {
                  term: "Account",
                  description: "Means a unique account created for you to access our Service or parts of our Service.",
                },
                {
                  term: "Affiliate",
                  description:
                    "Means an entity that controls, is controlled by, or is under common control with a party.",
                },
                {
                  term: "Application",
                  description:
                    "Refers to WhoPlays US – Pickleball Scheduler & Tracker, the software program provided by the Company.",
                },
                {
                  term: "Company",
                  description:
                    '("the Company", "We", "Us", or "Our") refers to WhoPlays US, Inc, 45 Whistler Trace, Ponte Vedra FL 32081.',
                },
                {
                  term: "Country",
                  description: "Refers to: Florida, United States.",
                },
                {
                  term: "Device",
                  description:
                    "Means any device that can access the Service such as a computer, a cellphone, or a digital tablet.",
                },
                {
                  term: "Personal Data",
                  description: "Is any information that relates to an identified or identifiable individual.",
                },
                { term: "Service", description: "Refers to the Application." },
                {
                  term: "Service Provider",
                  description:
                    "Means any natural or legal person who processes the data on behalf of the Company.",
                },
                {
                  term: "Usage Data",
                  description:
                    "Refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself.",
                },
                {
                  term: "You",
                  description: "Means the individual accessing or using the Service.",
                },
              ],
            },
          ],
        },
        {
          heading: "Collecting and Using Your Personal Data",
          subSections: [
            {
              subheading: "Personal Data",
              paragraphs: ["We may collect personal information such as:"],
              bullets: ["Email address", "First name and last name", "Phone number", "Address, State, Province, ZIP/Postal code, City"],
            },
            {
              subheading: "Usage Data",
              paragraphs: [
                "This includes your IP address, browser type and version, the pages you visit, the time and date of your visit, the time spent on those pages, and other diagnostic data.",
              ],
            },
            {
              subheading: "Data Collected via Application",
              paragraphs: [
                "With your permission, we may collect additional information:",
              ],
              bullets: [
                "Location data",
                "Contacts (for invite functionality)",
                "Access to camera and photo library (for profile pictures or event photos)",
              ],
              footnote:
                "You can enable or disable access to this data at any time through your device settings.",
            },
            {
              subheading: "Use of Your Personal Data",
              paragraphs: ["We use your data for the following purposes:"],
              bullets: [
                "To provide and maintain our Service",
                "To manage your account",
                "To facilitate SMS invitations and app notifications",
                "To manage subscriptions and validate user entitlements",
                "To provide support and respond to inquiries",
                "To inform you about updates, offers, and changes",
                "To analyze performance and usage for improvements",
              ],
            },
            {
              subheading: "Subscription Services",
              paragraphs: [
                "WhoPlays US offers free, basic, and pro-tier subscriptions alongside tournament-specific subscriptions. For promotional purposes, some users may receive lifetime access. We reserve the right to revoke such access if the user has not scheduled an event or used key features within a 60-day period.",
                "In-app subscriptions may be managed through the Apple App Store or Google Play. These subscriptions follow the billing, cancellation, and renewal policies outlined by those platforms.",
              ],
            },
          ],
        },
        {
          heading: "Data Retention and Deletion",
          paragraphs: [
            "Your data is retained only as long as needed for legal, operational, and analytical purposes.",
            "You may delete your account or request deletion of your data via the app or by contacting us.",
          ],
        },
        {
          heading: "Legal Disclosure and Security",
          paragraphs: [
            "We may disclose your data when required by law, to protect our rights, or to ensure user safety.",
            "We use commercially acceptable means to protect your data but cannot guarantee absolute security.",
          ],
        },
        {
          heading: "Children's Privacy",
          paragraphs: [
            "We do not knowingly collect information from anyone under 13. If you believe we have collected data from a child, please contact us.",
          ],
        },
        {
          heading: "Changes to This Privacy Policy",
          paragraphs: [
            "We may update this Privacy Policy. If material changes are made, you will be notified via email or in-app notice.",
            "All changes are effective when posted.",
          ],
        },
      ],
      contact: {
        heading: "Contact Us",
        paragraphs: ["If you have any questions, please contact us at:"],
        entries: [{ label: "Email", value: "support@whoplays.us" }],
      },
    },
    mySchedule: {
      back: 'Back to app',           // es: 'Volver a la app', pt: 'Voltar ao app'
      title: 'My Play Schedule',     // es: 'Mi agenda', pt: 'Minha agenda'
      create: 'Create Event',        // es: 'Crear evento', pt: 'Criar evento'
      edit: 'Edit',                  // es: 'Editar', pt: 'Editar'
      delete: 'Delete',              // es: 'Eliminar', pt: 'Excluir'
      empty: 'No scheduled plays yet.', // es: 'Sin partidos programados.', pt: 'Sem partidas agendadas.'
      untitled: 'Scheduled Match',   // es: 'Partido programado', pt: 'Partida agendada'
      startLabel: 'Start',
      endLabel: 'End',
      typeLabel: 'Type',
      courtLabel: 'Court',
      loading: 'Loading schedule...',
      refreshError: 'Unable to refresh your schedule.',
      cancel: 'Cancel',
      confirmCancel: 'Cancel this schedule?',
    },
    myScheduleEditor: {
      titleCreate: 'Schedule Event',
      titleEdit: 'Edit Schedule',
      subtitle: 'Set your time, court, format, and invites.',
      encourage: 'Invite friends—even if they are not on the app—to make your match more fun.',
      dateLabel: 'Date',
      startLabel: 'Start time',
      endLabel: 'End time',
      courtLabel: 'Court',
      formatLabel: 'Format',
      invitesLabel: 'Invitations',
      invitesCount: 'Invited: {count}',
      inviteCta: 'Invite Players ({count})',
      limitToggle: 'Enable confirmation limit',
      limitLabel: 'Set Limit',
      limitHelper: 'Starts at 4 once a limit is enabled.',
      limitLocked: 'Set Limit unlocks after inviting 4 players.',
      saveCreate: 'Save Schedule',
      saveEdit: 'Save Changes',
      reset: 'Reset',
      validation: {
        dateRequired: 'Please select a date.',
        courtRequired: 'Please select a court.',
        endAfterStart: 'Please select an end time after the start time.',
        conflict: 'You already have a scheduled play at this time.',
        saveError: 'Failed to save. Please try again.',
        saveSuccess: 'Schedule saved successfully.',
      },
      noCourt: 'You must have a Primary court to schedule a play.',
    },
    myScheduleInvite: {
      title: 'Send Invites',
      selected: 'Selected {count} invitees',
      back: 'Back to schedule',
      addGuest: 'Add Guest',
      playerNamePlaceholder: 'Player name',
      phonePlaceholder: 'Phone number',
      add: 'Add',
      contactsUnavailable: 'Add from Contacts (unavailable)',
      contactsAvailable: 'Add from Contacts',
      myGroup: 'My Group',
      following: 'Following',
      searchPlaceholder: 'Search players',
      noPlayers: 'No players found.',
      validation: {
        nameEmpty: 'Name cannot be empty. Please enter the name of the guest.',
        phoneEmpty: 'Phone number cannot be empty. Please enter a valid phone number.',
        phoneNumeric: 'Phone number can only contain numeric characters.',
        phoneLength: 'Invalid USA phone number. It must contain exactly 10 digits.',
        phoneInvalid: 'Invalid phone number.',
        selfNumber: 'You have added your own number. You are already included in this schedule as the host.',
        duplicateGuest: 'This player has already been added as a guest to this schedule.',
        registered: 'This player is already registered in WhoPlays. We have added their existing account: {name}.',
      },
    },
  },
  es: {
    languageName: "Español",
    nav: {
      features: "Características",
      workflow: "Cómo funciona",
      plans: "Planes",
      organizers: "Organizadores fundadores",
      login: "Iniciar sesión",
      signup: "Crear cuenta",
    },
    auth: {
      otp: {
        userNotFound: "No encontramos una cuenta con ese correo electrónico o número de teléfono.",
      },
    },
    toggles: {
      theme: "Tema",
      language: "Idioma",
      light: "Claro",
      dark: "Oscuro",
      system: "Sistema",
    },
    hero: {
      eyebrow: "Jugadores y organizadores de pickleball unidos",
      title: "La app de pickleball que mantiene tus canchas activas",
      description:
        "WhoPlays es la app de pickleball para canchas públicas, clubes y programas municipales. Controla la actividad en vivo, envía invitaciones por SMS y haz crecer tu comunidad de pickleball en un solo lugar.",
      primaryCta: "Descargar la app de pickleball",
      primaryHref: "/app-stores",
      secondaryCta: "Ser organizador fundador",
      secondaryHref: "/founding-organizers",
      trust: "Confiado por comunidades de pickleball en canchas públicas y privadas.",
    },
    stats: [
      { value: "Feed de canchas", label: "Mira cuándo se activan las canchas de pickleball cerca de ti" },
      { value: "Invitaciones SMS", label: "Llena tus sesiones de pickleball con un toque y confirmaciones por texto" },
      { value: "Estadísticas", label: "Registra partidos de pickleball y sigue tu avance en DUPR" },
    ],
    appShell: {
      tabs: {
        realtime: "En vivo",
        network: "Mi red",
        competitions: "Competiciones",
        matches: "Partidos",
        rank: "Ranking",
        messages: "Centro MSG",
      },
      matches: {
        title: "Partidos",
        pending: "Pendientes",
        addMatch: "Agregar partido",
        loadMore: "Cargar más",
        loading: "Cargando partidos...",
        empty: "No hay partidos para mostrar.",
        segments: {
          owned: "Propios",
          mygroup: "Mi grupo",
          mycourts: "Mis sedes",
        },
        errors: {
          loadFailed: "No se pueden cargar los partidos ahora.",
          saveFailed: "No se pudo guardar el partido.",
        },
        editor: {
          title: "Nuevo partido",
          subtitle: "Agrega marcadores y jugadores.",
          cancel: "Cancelar",
          save: "Guardar",
          saving: "Guardando...",
          format: "Formato",
          singles: "Singles",
          doubles: "Dobles",
          winner: "Ganador",
          selectWinner: "Selecciona ganador",
          team1: "Equipo 1",
          team2: "Equipo 2",
          player: "Usuario",
          score: "Marcador",
        },
        validation: {
          missingPlayers: "Agrega todos los jugadores requeridos.",
          missingWinner: "Selecciona un ganador.",
        },
      },
      rank: {
        title: "Ranking",
        loading: "Cargando rankings...",
        empty: "No hay datos de ranking para mostrar.",
        segments: {
          wprank: "WP Rank",
          overall: "General",
          h2h: "H2H",
        },
        filters: {
          skillAll: "Todos los niveles",
          singles: "Singles",
          doubles: "Dobles",
          week: "Semana",
          month: "Mes",
          all: "Todo el tiempo",
          allCourts: "Todas las canchas",
          groupNone: "Sin filtro de grupo",
          groupMyGroup: "Mi grupo",
          groupFollowing: "Siguiendo",
          formatAll: "Todos los formatos",
          today: "Hoy",
          yesterday: "Ayer",
          lastWeek: "Semana pasada",
          lastMonth: "Mes pasado",
          threeMonths: "3 meses",
          sixMonths: "6 meses",
          year: "1 año",
          fiveYears: "5 años",
          allTime: "Todo el tiempo",
        },
        labels: {
          wins: "Ganados",
          losses: "Perdidos",
          matches: "Partidos",
          winPct: "Win%",
          rankLabel: "Ranking",
        },
      },
      messagesTab: {
        title: "Centro de mensajes",
        empty: "Aún no hay mensajes.",
        clearAll: "Borrar todo",
        markAllRead: "Marcar todo como leído",
        delete: "Eliminar",
        read: "Marcar leído",
        unread: "Marcar sin leer",
        receivedAt: "Recibido",
      },
      socket: {
        schedule: "Agenda actualizada",
        match: "Actualización de partido",
        ping: "Nuevo ping recibido",
        dupr: "DUPR actualizado",
        generic: "Nueva notificación",
        subscriptionTitle: "Suscripción actualizada",
        subscriptionBody: "Tu estado de suscripción se ha actualizado.",
        matchUnanimousTitle: "Partido aceptado",
        matchUnanimousBody: "¡Buenas noticias! Un partido que jugaste fue aceptado por todos. ¡Ya es oficial!",
        matchAcceptedTitle: "Jugador aceptó el partido",
        matchAcceptedBody: "{player} aceptó el partido. Esperando a los demás.",
        matchPendingTitle: "Partidos pendientes",
        matchPendingBody: "¡Nuevos resultados pendientes! Confirma para que cuente en tu ranking.",
        duprUpdatedTitle: "DUPR actualizado",
        duprUpdatedBody: "Singles: {singles}\nDobles: {doubles}",
        questionTitle: "Nueva pregunta de agenda",
      },
      subscriptions: {
        title: "Suscripciones",
        subtitle: "Elige un plan para potenciar tu programación en WhoPlays.",
        detailsLink: "Ver detalles completos del plan",
        currentPlan: "Plan actual",
        statusLabel: "Estado",
        free: "Gratis",
        active: "Activo",
        select: "Seleccionar",
        checkout: "Pagar con Stripe",
        close: "Cerrar",
        manage: "Gestionar / cancelar en Stripe",
        manageHint: "Abriremos el portal de Stripe. Si no ves tu suscripción, usa el correo del recibo.",
        providerManaged: "Gestionado por {provider}",
        providerManagedHint: "Para gestionar o cancelar esta suscripción, ve a {provider}.",
        error: "No pudimos iniciar el pago. Inténtalo nuevamente.",
        successTitle: "Pago completado",
        successToast: "Pago de suscripción completado. Actualizando tu acceso…",
        cancelTitle: "Pago cancelado",
        cancelToast: "Cancelaste el pago en Stripe.",
        plans: {
          "com.whoplays.scheduling.basic.monthly": {
            label: "Scheduler Básico (Mensual)",
            price: "Cobro mensual",
          },
          "com.whoplays.scheduling.basic.yearly": {
            label: "Scheduler Básico (Anual)",
            price: "Cobro anual",
          },
          "com.whoplays.scheduling.pro.monthly": {
            label: "Scheduler Pro (Mensual)",
            price: "Cobro mensual",
          },
          "com.whoplays.scheduling.pro.yearly": {
            label: "Scheduler Pro (Anual)",
            price: "Cobro anual",
          },
        },
      },
      subscriptionLimits: {
        title: "Límite alcanzado",
        sms: "Has alcanzado tu límite mensual de {limit} invitaciones por SMS.",
        players: "Has alcanzado el límite de {limit} jugadores por programación.",
        events: "Has alcanzado tu límite de {limit} programaciones activas con invitaciones.",
        generic: "Has alcanzado tu límite actual de {limit}.",
        upgrade: "Actualizar ahora",
        cancel: "Cancelar",
      },
      sidebar: {
        welcome: "Bienvenido de nuevo",
        kpiWins: "Victorias",
        kpiLosses: "Derrotas",
        kpiPending: "Pendientes",
        quickActions: {
          language: "Cambiar idioma",
          languageNext: "Siguiente: {language}",
          theme: "Cambiar tema",
          tips: "Consejos (pronto)",
          tipsSoon: "Los consejos estarán disponibles muy pronto.",
          logout: "Cerrar sesión",
        },
        toggles: {
          dnd: "No molestar",
          tips: "Consejos",
          share: "Compartir",
          language: "Idioma",
        },
        actions: {
          profile: "Perfil",
          preferredCourts: "Mis sedes",
          schedule: "Mi agenda",
          linkDupr: "Vincular DUPR®",
          subscriptions: "Suscripciones",
          invite: "Invitar jugadores",
          privacy: "Política de privacidad",
          settings: "Configuración y preferencias",
          appStats: "Estadísticas",
          logout: "Cerrar sesión",
        },
        admin: {
          label: "Admin",
          access: "Acceso y permisos",
        },
        chartLabel: "Relación victorias/derrotas",
        chartWins: "Victorias",
        chartLosses: "Derrotas",
        chartPending: "Pendientes",
        chartNoData: "Aún no hay historial de partidos.",
        chartWinRate: "Porcentaje de victorias",
        chartTotal: "Total",
        skillLevels: {
          beginner: "Principiante",
          intermediate: "Intermedio",
          advanced: "Avanzado",
        },
        rankings: {
          wpRankLabel: "Rango WP",
          duprLabel: "DUPR",
          singlesShort: "S",
          doublesShort: "D",
          notRated: "NR",
        },
      },
      header: {
        subtitleRank: "Ranking · Global",
        moreLabel: "Menú",
        currentLabel: "Actual",
      },
      analytics: {
        tabSwitch: "cambio_de_tab",
        sidebarOpen: "sidebar_abierta",
        sidebarClose: "sidebar_cerrada",
      },
      dupr: {
        title: "Vincular con DUPR®",
        subtitle: "Inicia sesión en DUPR para sincronizar tus clasificaciones.",
        instructions: "Completa el inicio de sesión de DUPR abajo. No guardamos tu contraseña.",
        waiting: "Esperando inicio de sesión de DUPR...",
        processing: "Vinculando tu cuenta DUPR...",
        success: "Cuenta DUPR vinculada correctamente.",
        error: "No se pudo vincular tu cuenta DUPR en este momento.",
        originError: "Mensaje ignorado de un origen no confiable.",
        missingConfig: "La integración con DUPR no está configurada.",
        close: "Cerrar",
        unauthorized: "Debes iniciar sesión para vincular DUPR. Inicia sesión y vuelve a intentarlo.",
      },
      realTime: {
        ribbonLabel: "Tus sedes",
        refresh: "Actualizar",
        noCourtsTitle: "Agrega una sede preferida para ver la actividad en vivo.",
        courtSelectorLabel: "Seleccionar una sede",
        summary: {
          title: "Jugadores en {court}",
          allLevels: "Todos los niveles",
          advanced: "Avanzado",
          intermediate: "Intermedio",
          beginner: "Principiante",
          metrics: {
            willing: "Listos para jugar",
            privateGroups: "Grupos privados",
            following: "Siguiendo",
          },
          metricsShort: {
            willing: "Listos",
            privateGroups: "Privados",
            following: "Siguiendo",
          },
          updated: "Actualizado {time}",
        },
        whosNextTitle: "Quién sigue",
        myScheduleTitle: "Mi agenda",
        myQuestionsTitle: "Mis preguntas",
        day: {
          today: "Hoy",
          tomorrow: "Mañana",
        },
        metrics: {
          openPlay: "Open Play",
          event: "Evento",
          privateGroup: "Grupo privado",
        },
        carousels: {
          noData: "Sin actividad programada.",
          dotsLabel: "Ir a la tarjeta {index}",
        },
        detail: {
          noActivity: "No hay actividad programada para este tipo.",
          close: "Cerrar",
          profileTitle: "Perfil del jugador",
          openProfile: "Abrir perfil completo",
          unknownPlayer: "Jugador desconocido",
          status: {
            host: "Anfitrión",
            accepted: "Aceptado",
            pending: "Pendiente",
            declined: "Rechazado",
          },
        },
        schedule: {
          empty: "Sin partidos próximos.",
          badges: {
            invited: "Invitado",
            hosted: "Organizado por {name} en {court}",
            hostedSelf: "Organizado por mí en {court}",
          },
          cardTime: "Mi agenda: {time}",
          counts: {
            invited: "Invitados",
            confirmed: "Confirmados",
          },
          confirmedLine: "Confirmados: {confirmed}",
          confirmedLineWithLimit: "Confirmados: {confirmed} de {limit}",
        },
        questions: {
          empty: "Sin preguntas para esta sede.",
          status: {
            pending: "Pendiente",
            answered: "Respondido",
          },
        },
        emptyHero: {
          title: "¡Programa un partido e invita jugadores!",
          bullets: [
            "¡Mira quién viene — en vivo!",
            "Invita por SMS y sigue las respuestas",
            "Mantén a todos al día por SMS o en la app",
          ],
          cta: "Programar evento",
        },
        fabLabel: "Programar evento",
      },
      network: {
        title: "Mi Red",
        subtitle: "Encuentra y gestiona a tus jugadores.",
        searchPlaceholder: "Buscar jugadores",
        search: "Buscar",
        segments: {
          suggestions: "Sugerencias",
          following: "Siguiendo",
          followers: "Seguidores",
        },
        courtFilter: "Canchas",
        allCourts: "Todas las canchas",
        loading: "Cargando jugadores…",
        error: "No se pueden cargar los jugadores ahora.",
        empty: "No hay jugadores para mostrar.",
        unknownSkill: "Nivel desconocido",
        follow: "Seguir",
        following: "Siguiendo",
        refresh: "Actualizar",
        loadMore: "Cargar más",
        actionError: "No se pudo actualizar el seguimiento.",
        levelCompetition: {
          recreational: "Recreativo",
          competitive: "Competitivo",
        },
        followTitle: "Seguir a {name}",
        followingTitle: "Siguiendo a {name}",
        close: "Cerrar",
        toggleGroup: "Agregar a estadísticas de Mi Grupo",
        toggleNotify: "Avisarme sobre este jugador",
        unfollow: "Dejar de seguir",
        save: "Guardar",
      },
    },
    profileEdit: {
      loading: "Cargando perfil…",
      header: {
        title: "Editar perfil",
        back: "Volver",
        cancel: "Cancelar",
        save: "Guardar preferencias",
        saving: "Guardando…",
      },
      sections: {
        avatar: {
          title: "Avatar",
          subtitle: "Sube una foto clara para que los jugadores te reconozcan.",
          button: "Cambiar foto",
          uploading: "Subiendo…",
          helper: "JPG o PNG hasta 5 MB.",
        },
        personal: { title: "Información personal" },
        location: { title: "Ubicación" },
        preferences: { title: "Preferencias y nivel" },
        bio: { title: "Biografía", placeholder: "Comparte una breve introducción" },
      },
      fields: {
        name: "Nombre completo",
        nickname: "Apodo",
        email: "Correo electrónico",
        phone: "Número de teléfono",
        country: "País",
        state: "Estado",
        city: "Ciudad",
        zipcode: "Código postal",
        status: "Estado",
        preferredLevel: "Nivel preferido",
        matchType: "Tipo de partido",
        skill: "Nivel de habilidad",
        selfRanking: "Auto clasificación",
        competition: "Nivel de competencia",
        defaultAudience: "Audiencia predeterminada",
        followerPrivacy: "Privacidad de seguidores",
      },
      helpers: {
        emailOptional: "El correo es opcional y puede usarse para notificaciones.",
        emailLogin: "Cambiar tu correo puede requerir verificación.",
        phoneLocked: "Este número de teléfono es tu inicio de sesión y no puede cambiarse.",
        phoneFormat: "Usa formato internacional, por ejemplo +1…",
      },
      success: {
        saved: "Perfil guardado.",
        photo: "Foto de perfil actualizada.",
        email: "Correo verificado.",
        phone: "Teléfono verificado.",
      },
      errors: {
        name: "El nombre es obligatorio.",
        email: "Introduce un correo electrónico válido.",
        phone: "Introduce un número de teléfono válido en formato internacional.",
        code: "Introduce el código de 6 dígitos.",
        save: "No se pudo guardar el perfil.",
        upload: "No se pudo subir el avatar.",
        verificationStart: "No se pudo iniciar la verificación.",
        verificationConfirm: "No se pudo verificar el código.",
      },
      verification: {
        verifyEmail: "Verificar correo",
        reverifyEmail: "Reverificar correo",
        verifyPhone: "Verificar teléfono",
        reverifyPhone: "Reverificar teléfono",
        badge: "Verificado",
        modalTitle: "Ingresa el código de verificación",
        modalDescriptionEmail: "Enviamos un código de 6 dígitos a {target}. Escríbelo para verificar tu correo.",
        modalDescriptionPhone: "Enviamos un código de 6 dígitos a {target}. Escríbelo para verificar tu teléfono.",
        submit: "Verificar",
        submitting: "Verificando…",
        cancel: "Cancelar",
      },
      options: {
        competition: [
          { value: 1, label: "Recreativo" },
          { value: 2, label: "Competitivo" },
        ],
      },
      legal: "WhoPlays US · Configuración de perfil",
    },
    myVenues: {
      back: "Volver a la app",
      title: "Mis sedes",
      subtitle: "Elige tu sede principal y guarda una lista de los lugares donde más juegas.",
      loading: "Cargando sedes…",
      primary: {
        heading: "Sede principal",
        empty: "Todavía no seleccionaste una sede principal.",
        required: "Selecciona una sede principal para personalizar tu experiencia.",
        clear: "Quitar sede principal",
      },
      preferred: {
        heading: "Sedes preferidas",
        empty: "Agrega las sedes donde juegas con frecuencia para tenerlas a mano.",
        remove: "Eliminar",
        makePrimary: "Convertir en principal",
        add: "Agregar a preferidas",
        countLabel: "seleccionadas",
      },
      badges: {
        primary: "Principal",
        preferred: "Preferida",
      },
      search: {
        heading: "Buscar sedes",
        placeholder: "Busca por nombre de sede o dirección",
        action: "Buscar",
        clear: "Limpiar búsqueda",
        useLocation: "Usar mi ubicación",
        locating: "Buscando…",
        locationReady: "Ubicación lista",
        locationError: "No pudimos obtener tu ubicación.",
        prompt: "Busca una sede o comparte tu ubicación para ver opciones cercanas.",
        noResults: "No encontramos sedes con esa búsqueda. Prueba con otros términos.",
        loadMore: "Cargar más resultados",
        loading: "Cargando sedes…",
        setPrimary: "Convertir en principal",
        addPreferred: "Agregar a preferidas",
        activeLabel: "Resultados para “{query}”",
        error: "No pudimos buscar sedes en este momento.",
        viewDetails: "Ver detalles",
        hideDetails: "Ocultar detalles",
      },
      actions: {
        save: "Guardar sedes",
        saving: "Guardando sedes…",
        saved: "Sedes actualizadas correctamente.",
        error: "No se pudieron guardar las sedes.",
        suggest: "Sugerir una sede",
        reviewPending: "Revisar sedes pendientes",
        warning: "Las sedes se guardaron, pero no pudimos actualizar los datos.",
      },
    },
    adminVenues: {
      back: "Volver a Mis sedes",
      title: "Sedes pendientes de verificación",
      subtitle: "Revisa las sedes recién enviadas antes de publicarlas.",
      loading: "Cargando sedes pendientes…",
      empty: "No hay sedes en espera de verificación.",
      countLabel: "Sedes pendientes: {count}",
      refresh: "Actualizar lista",
      error: "No pudimos cargar las sedes pendientes.",
      reviewCta: "Abrir revisión",
      hoursHeading: "Horario de atención",
      courtsHeading: "Configuración de canchas",
      totalCourts: "{count} canchas en total",
      amenitiesHeading: "Servicios",
      openPlayHeading: "Sesiones de open play hoy",
      pendingBadge: "Pendiente de verificación",
    },
    adminAccess: {
      back: "Volver al panel",
      title: "Acceso y permisos",
      subtitle: "Concede roles, roles de sede y acceso a suscripciones.",
      loading: "Cargando datos de acceso…",
      error: "No pudimos cargar el resumen de administración.",
      refresh: "Actualizar",
      searchPlaceholder: "Buscar usuarios o sedes",
      usersEmpty: "No hay usuarios que coincidan con los filtros.",
      courtsEmpty: "No hay sedes que coincidan con los filtros.",
      adminOnly: "Solo administradores.",
      unknownCourt: "Sede desconocida",
      tabs: {
        users: "Usuarios",
        courts: "Sedes",
      },
      filters: {
        all: "Todas",
        roles: "Roles",
        courtRoles: "Roles de sede",
        subscriptions: "Suscripciones",
      },
      roles: {
        heading: "Roles",
        empty: "Sin roles todavía.",
        add: "Agregar rol",
        select: "Selecciona un rol",
      },
      courtRoles: {
        heading: "Roles de sede",
        empty: "Sin roles de sede.",
        add: "Agregar rol de sede",
        update: "Actualizar rol",
        selectCourt: "Selecciona una sede",
        selectUser: "Selecciona un usuario",
        roleLabel: "Rol",
        searchPlaceholder: "Buscar sedes",
        searchUsersPlaceholder: "Buscar usuarios",
      },
      access: {
        heading: "Acceso a suscripciones",
        empty: "Sin accesos concedidos.",
        add: "Agregar acceso",
        update: "Actualizar acceso",
        selectPlan: "Selecciona un plan",
        expiresAt: "Vence el",
      },
      actions: {
        remove: "Eliminar",
        edit: "Editar",
        close: "Cerrar",
      },
      rules: {
        adminOnly: "Solo super admins pueden agregar o quitar el rol admin.",
      },
    },
    adminStats: {
      back: "Volver al panel",
      title: "Estadisticas de la app",
      subtitle: "Monitorea actividad y crecimiento.",
      loading: "Cargando estadisticas...",
      error: "No pudimos cargar el resumen de estadisticas.",
      adminOnly: "Solo administradores.",
      periodLabel: "Periodo",
      controls: {
        period: "Periodo",
        timezone: "Zona horaria",
        topN: "Top N",
        pendingLimit: "Limite de pendientes",
        apply: "Aplicar",
        periodLabel: "Periodo seleccionado",
      },
      sections: {
        lifetime: "Acumulado",
        period: "Periodo",
        activation: "Embudo de activacion",
        events: "Eventos",
        engagement: "Actividad",
        geo: "Geografia",
        pendingVenues: "Sedes pendientes",
        subscriptions: "Suscripciones",
        subscriptionsPeriod: "Suscripciones (Periodo)",
        downloadsByOs: "Descargas por sistema",
      },
      metrics: {
        uniqueUsers: "Usuarios",
        uniqueDownloads: "Descargas",
        profilesCreated: "Perfiles creados",
        primaryCourtSelected: "Sede principal seleccionada",
        sessionsCount: "Sesiones",
        eventsCount: "Eventos",
        sessionsDuration: "Duracion de sesiones",
        sessionsDurationLifetime: "Duracion acumulada",
      },
      empty: {
        downloads: "Sin descargas.",
      },
      funnel: {
        ofDownloads: "de descargas",
        ofPrevious: "del paso anterior",
      },
    },
    suggestVenue: {
      backToVenues: "Volver a Mis sedes",
      title: "Sugerir una sede",
      subtitle: "Comparte los datos de un nuevo lugar para que nuestro equipo lo revise.",
      steps: {
        basic: "Básicos",
        details: "Detalles",
        openPlay: "Open play",
        photos: "Fotos",
        contact: "Contacto",
        review: "Revisión",
      },
      actions: {
        previous: "Atrás",
        next: "Siguiente",
        submit: "Enviar sugerencia",
        submitting: "Enviando…",
        approve: "Aprobar y publicar",
        markPending: "Marcar como pendiente",
        delete: "Eliminar sede",
      },
      loadingExisting: "Cargando datos de la sede…",
      successMessage: "¡Gracias! Recibimos tu sugerencia de sede.",
      standaloneSuccess: {
        title: "¡Gracias por enviarnos una nueva sede!",
        body: "Nuestro equipo revisará los datos y normalmente publicará la sede dentro de 24 a 72 horas una vez que todo esté validado.",
        helper: "Te contactaremos si necesitamos más información.",
        ctaPrimary: "Sugerir otra sede",
        ctaSecondary: "Cerrar",
      },
      successApproved: "Sede aprobada y publicada.",
      successPending: "Marcada como pendiente de actualizaciones.",
      successDeleted: "Sede eliminada.",
      errors: {
        basicRequired: "Ingresa el nombre y la dirección completa antes de continuar.",
        detailsRequired: "Agrega al menos una configuración de cancha y el tipo de superficie.",
        descriptionRequired: "Describe la sede para que otros jugadores sepan qué esperar.",
        contactRequired: "Necesitamos tus datos de contacto por si debemos escribirte.",
        confirmInformation: "Confirma que la información proporcionada es correcta.",
        submitFailed: "No pudimos guardar la sugerencia. Inténtalo más tarde.",
        loadVenue: "No pudimos cargar los datos de la sede. Inténtalo nuevamente.",
        deleteFailed: "No pudimos eliminar la sede. Inténtalo nuevamente.",
      },
      confirmDelete: "¿Eliminar esta sede? Esta acción no se puede deshacer.",
      basic: {
        heading: "Información básica",
        subheading: "Comienza con el nombre y la dirección para ubicar la sede.",
        nameLabel: "Nombre de la sede",
        namePlaceholder: "Complejo Riverside Pickleball",
        shortNameLabel: "Nombre corto",
        shortNamePlaceholder: "Etiqueta opcional",
        addressLabel: "Dirección",
        addressPlaceholder: "Calle Principal 123",
        cityLabel: "Ciudad",
        stateLabel: "Estado / región",
        zipLabel: "Código postal",
        countryLabel: "País",
        countryPlaceholder: "USA",
        latitudeLabel: "Latitud",
        longitudeLabel: "Longitud",
        mapHeading: "Vista previa del mapa",
        mapHelper: "Ingresa latitud y longitud para ver la ubicación.",
        mapMissing: "Agrega coordenadas para mostrar el mapa.",
        mapTitle: "Vista previa del mapa para {name}",
        mapDefaultName: "esta sede",
        mapLoadError: "No pudimos cargar el mapa. Verifica tu conexión e intenta nuevamente.",
        mapSearchingForward: "Buscando la ubicación para esta dirección…",
        mapSearchingReverse: "Buscando la dirección para estas coordenadas…",
        mapGeocodeError: "No encontramos coordenadas para esa dirección. Intenta ser más específico.",
        mapPendingHeading: "¿Actualizar la dirección con esta ubicación?",
        mapPendingApply: "Usar esta dirección",
        mapPendingIgnore: "Conservar dirección actual",
      },
      details: {
        heading: "Detalles de la sede",
        subheading: "Indica el tipo de cancha, superficie y servicios disponibles.",
        surfaceLabel: "Superficie principal",
        surfacePlaceholder: "Elige superficie",
        typeLabel: "Tipo de sede",
        typePublic: "Pública (abierta a todos)",
        typePrivate: "Privada (requiere membresía)",
        courtHeading: "Configuración de canchas",
        courtAdd: "Agregar fila",
        courtTypeLabel: "Tipo de cancha",
        courtTypePlaceholder: "Selecciona tipo",
        courtTypeDedicated: "Dedicada",
        courtTypeShared: "Red compartida",
        courtTypeLined: "Solo líneas",
        courtEnvironmentLabel: "Ambiente",
        courtEnvironmentPlaceholder: "Selecciona ambiente",
        environmentIndoor: "Interior",
        environmentOutdoor: "Exterior",
        environmentCovered: "Cubierta",
        courtQuantityLabel: "Cantidad",
        courtRemove: "Eliminar fila",
        amenitiesHeading: "Servicios",
        amenitiesOptions: [
          "Alumbrado",
          "Baños",
          "Puntos de agua",
          "Asientos",
          "Pro Shop",
          "Casilleros",
          "Duchas",
          "Comida/Bebida",
          "Estacionamiento",
          "Acceso para sillas de ruedas",
          "Áreas con sombra",
          "Lanzapelotas",
          "Clases disponibles",
        ],
        hoursHeading: "Horario de atención",
        timeSlots: [
          "5:00 AM",
          "6:00 AM",
          "7:00 AM",
          "8:00 AM",
          "9:00 AM",
          "10:00 AM",
          "11:00 AM",
          "12:00 PM",
          "1:00 PM",
          "2:00 PM",
          "3:00 PM",
          "4:00 PM",
          "5:00 PM",
          "6:00 PM",
          "7:00 PM",
          "8:00 PM",
          "9:00 PM",
          "10:00 PM",
          "11:00 PM",
          "12:00 AM",
        ],
        until: "a",
        closedLabel: "Cerrado",
        descriptionLabel: "Descripción",
        descriptionPlaceholder: "Cuenta características, estado de las canchas y recomendaciones para los jugadores.",
        phoneLabel: "Teléfono",
        phonePlaceholder: "(Opcional) Número de contacto",
        websiteLabel: "Sitio web",
        websitePlaceholder: "https://…",
      },
      openPlay: {
        heading: "Horarios de open play",
        subheading: "Agrega horarios de juego libre para que otros sepan cuándo ir.",
        sessionsCount: "{count} sesiones",
        copyWeekdays: "Copiar a lunes-viernes",
        copyWeekend: "Copiar a fin de semana",
        addSession: "Agregar sesión",
        emptyLabel: "Aún no hay open play cargado para este día.",
        startLabel: "Inicio",
        endLabel: "Fin",
        levelLabel: "Nivel",
        statusLabel: "Estado",
        removeSession: "Eliminar sesión",
        timeSlots: [
          "6:00 AM",
          "7:00 AM",
          "8:00 AM",
          "9:00 AM",
          "10:00 AM",
          "11:00 AM",
          "12:00 PM",
          "1:00 PM",
          "2:00 PM",
          "3:00 PM",
          "4:00 PM",
          "5:00 PM",
          "6:00 PM",
          "7:00 PM",
          "8:00 PM",
          "9:00 PM",
          "10:00 PM",
        ],
        levelOptions: ["Todos los niveles", "Principiante", "Principiante+", "Intermedio", "Intermedio+", "Avanzado", "Jugadores de torneo"],
        statusOptions: ["Disponible", "Pocos cupos", "Completo", "Solo socios", "Abierto al público", "Requiere registro"],
      },
      photos: {
        heading: "Fotos de la sede",
        subheading: "Sube imágenes para que los jugadores reconozcan el lugar.",
        helper: "Arrastra y suelta imágenes o selecciona archivos de tu dispositivo.",
        uploadCta: "Elegir fotos",
        supported: "Formatos JPG o PNG (máx 5 MB por archivo).",
        current: "Fotos existentes",
        pending: "Fotos para subir",
        remove: "Quitar",
      },
      contact: {
        heading: "Información de contacto",
        subheading: "Cuéntanos a quién podemos contactar si necesitamos más detalles.",
        nameLabel: "Tu nombre",
        emailLabel: "Correo electrónico",
        phoneLabel: "Teléfono",
        roleLabel: "Tu relación con la sede",
        rolePlaceholder: "Selecciona tu rol",
        roleOwner: "Propietario / operador",
        roleManager: "Administrador",
        roleEmployee: "Empleado",
        rolePlayer: "Jugador habitual",
        roleVisitor: "Visitante",
        roleOther: "Otro",
        notesLabel: "Notas adicionales",
        notesPlaceholder: "Información extra que ayude al equipo de revisión.",
        confirmationLabel: "Confirmo que la información es correcta y respeta las normas de la comunidad.",
      },
      review: {
        heading: "Revisar y enviar",
        subheading: "Verifica todo antes de enviar la sugerencia.",
        basic: "Información básica",
        name: "Nombre de la sede",
        address: "Dirección",
        type: "Tipo de sede",
        surface: "Superficie",
        courts: "Canchas",
        amenities: "Servicios",
        description: "Descripción",
        photos: "Fotos",
        contact: "Contacto",
        contactName: "Nombre",
        contactEmail: "Correo",
        contactPhone: "Teléfono",
        contactRole: "Rol",
        notes: "Notas",
        unknown: "Desconocido",
      },
      duplicates: {
        title: "Esta sede podría existir",
        description: "Encontramos sedes similares. Si alguna coincide, ábrela en lugar de crear un duplicado.",
        view: "Ver sede",
        continue: "Continuar de todas formas",
        cancel: "Volver",
      },
    },
    settings: {
      header: {
        title: "Configuración y preferencias",
        description: "Controla apariencia, privacidad y notificaciones en WhoPlays.",
        back: "Volver",
        cancel: "Cancelar",
        save: "Guardar preferencias",
        saving: "Guardando…",
      },
      guard: {
        title: "Configuración no disponible",
        description: "Completa tu perfil para desbloquear la configuración.",
        cta: "Editar perfil",
      },
      theme: {
        title: "Preferencia de tema",
        description: "Elige cómo se ve WhoPlays en este dispositivo.",
        options: {
          system: { label: "Sistema", description: "Usar configuración del dispositivo" },
          light: { label: "Claro", description: "Interfaz luminosa" },
          dark: { label: "Oscuro", description: "Ideal para poca luz" },
        },
      },
      language: {
        title: "Idioma",
        description: "Cambia el idioma de la interfaz al instante.",
      },
      availability: {
        title: "Disponibilidad",
        options: {
          1: { title: "Disponible", description: "Los jugadores pueden invitarte a partidos." },
          2: { title: "No disponible", description: "Aparece desconectado para todos." },
          6: { title: "Grupo privado", description: "Visible solo para miembros de grupos privados." },
        },
      },
      visibility: {
        title: "Visibilidad del perfil",
        options: {
          1: { title: "Público", description: "Cualquiera puede ver tu perfil." },
          3: { title: "Usuarios registrados", description: "Solo miembros de WhoPlays pueden ver tu perfil." },
          4: { title: "Conexiones", description: "Solo tus conexiones ven todos los detalles." },
          5: { title: "Privado", description: "Solo tú y administradores ven tu perfil." },
        },
      },
      followers: {
        title: "Visibilidad para seguidores",
        options: {
          1: { title: "Público", description: "Los seguidores ven tu actividad pública." },
          2: { title: "Cancha principal", description: "Limita las actualizaciones a tu comunidad principal." },
          3: { title: "Todas las canchas", description: "Los seguidores ven todas tus actualizaciones." },
          4: { title: "Amigos", description: "Solo amigos aprobados ven tus novedades." },
        },
      },
      notifications: {
        title: "Notificaciones",
        description: "Elige cómo deseas recibir información de WhoPlays.",
        items: {
          tournamentReminders: { title: "Recordatorios de torneos", description: "Recibe avisos antes de tus torneos registrados." },
          matchNotifications: { title: "Actualizaciones de partidos", description: "Avisos cuando se programan o cambian partidos." },
          partnerRequests: { title: "Solicitudes de compañero", description: "Entérate cuando quieran conectarse contigo." },
          communityEvents: { title: "Eventos comunitarios", description: "Mantente al tanto de eventos cercanos." },
          emailNotifications: { title: "Notificaciones por correo", description: "Recibe actualizaciones también por email." },
          eventInvitesSms: { title: "Invitaciones por SMS", description: "Recibe invitaciones por texto para responder rápido." },
          matchInvitesSms: { title: "Alertas de partidos por SMS", description: "Recibe recordatorios y detalles por SMS." },
          scoringUpdatesSms: { title: "Marcador por SMS", description: "Confirma resultados desde mensajes de texto." },
        },
      },
      account: {
        title: "Accesos directos de cuenta",
        links: {
          connectedAccounts: { title: "Cuentas conectadas", description: "Gestiona tus redes vinculadas." },
          dataPrivacy: { title: "Datos y privacidad", description: "Revisa cómo gestionamos tu información." },
          deleteAccount: { title: "Eliminar cuenta", description: "Elimina tu cuenta y datos de forma permanente." },
        },
      },
      messages: {
        saved: "Preferencias guardadas correctamente.",
        error: "No pudimos guardar tus preferencias. Inténtalo de nuevo.",
      },
    },
    homeVenues: {
      title: "Sedes cerca de ti",
      description:
        "Busca sedes WhoPlays por nombre o dirección y revisa las sesiones de juego abierto de hoy.",
      pageTitle: "Directorio de sedes de pickleball",
      pageDescription:
        "Busca por nombre o dirección, revisa servicios y descubre sesiones de juego abierto en tu zona.",
      useMyLocation: "Usar mi ubicación",
      locating: "Detectando ubicación...",
      searchPlaceholder: "Buscar por nombre o dirección",
      searchAriaLabel: "Buscar sedes",
      searchCta: "Buscar",
      promptLocation: "Comparte tu ubicación o busca por nombre para encontrar sedes cercanas.",
      clearFilters: "Limpiar filtros",
      errorLoading: "No pudimos cargar las sedes cercanas. Inténtalo de nuevo.",
      noResults: "No hay sedes que coincidan con tu búsqueda.",
      loading: "Cargando sedes...",
      loadMore: "Cargar más",
      loadingMore: "Cargando más sedes...",
      distanceAway: "A {distance}",
      verifiedBadge: "Verificada",
      courtsHeading: "Detalle de canchas",
      courtsHeadingWithTotal: "Detalle de canchas · {count} canchas",
      courtsFallback: "{count} canchas en total",
      surfaceLabel: "Superficie principal: {surface}",
      totalCourts: "{count} canchas en total",
      amenitiesHeading: "Servicios",
      openPlayHeading: "Juego abierto de hoy",
      hoursHeading: "Horario de operación",
      hoursClosed: "Cerrado",
      contactPhone: "Llamar al {phone}",
      visitWebsite: "Visitar sitio web",
      viewOnMap: "Ver en el mapa",
      viewAllVenues: "Ver todas las sedes",
      viewDetails: "Ver detalle",
      backToHome: "Volver al inicio",
      openStatusNow: "Abierta ahora",
      closedStatusNow: "Cerrada ahora",
      closedStatusToday: "Cerrada hoy",
      openUntil: "Abierta hasta las {time}",
      opensAt: "Abre a las {time}",
      closedForDay: "Cerrada por hoy",
      statusUnknown: "Estado no disponible",
      statusClosedTemporary: "Cerrada temporalmente",
      statusClosedPermanent: "Cerrada permanentemente",
      statusClosedOutOfSeason: "Cerrada fuera de temporada",
      publicVenue: "Pública",
      privateVenue: "Privada",
      mapFallbackTitle: "Mapa de {name}",
      mapFallbackNoLocation: "Sin datos de ubicación.",
    },
    features: {
      title: "Todo lo que necesita tu comunidad pickleball",
      description:
        "Sigue canchas, envía invitaciones y registra partidos con herramientas creadas para el juego en canchas públicas.",
      cards: [
        {
          title: "Visión en vivo de canchas",
          description:
            "Conoce el movimiento antes de salir de casa y comparte un resumen para que todos sepan qué está pasando.",
          points: [
            "Sigue canchas públicas y mira quién juega según el nivel",
            "Revisa sesiones programadas y juegos abiertos en un solo lugar",
            "Publica avisos rápidos para notificar a quienes siguen la cancha",
          ],
        },
        {
          title: "Sesiones rápidas e invitaciones SMS",
          description:
            "Crea un juego, ladder o entrenamiento en segundos e invita por texto. Los jugadores responden Sí/No y la lista se actualiza sola.",
          points: [
            "Plantillas flexibles para juegos abiertos, ladders o clínicas",
            "Invita por SMS sin obligar a instalar la app",
            "Recibe respuestas en tiempo real y envía recordatorios automáticos",
          ],
        },
        {
          title: "Perfiles y estadísticas",
          description:
            "Registra tus partidos, vincula tu DUPR y arma tu red de compañeros, rivales y nuevos contactos.",
          points: [
            "Guarda marcadores, partidos verificados y notas personales",
            "Autoevalúa tu nivel o sincroniza con DUPR para ver tu rating",
            "Sigue jugadores, crea chats y arma tu cuadrilla habitual",
          ],
        },
      ],
    },
    social: {
      title: "Sigue a la comunidad WhoPlays de pickleball",
      description:
        "Consejos, novedades del producto y momentos detrás de cámaras de los jugadores que construyen la app WhoPlays.",
      links: [
        { name: "Instagram", handle: "@whoplaysus", href: "https://www.instagram.com/whoplaysus/" },
        { name: "Facebook", handle: "WhoPlays Pickleball", href: "https://www.facebook.com/profile.php?id=61550796384597" },
        { name: "YouTube", handle: "WhoPlays Pickleball", href: "https://www.youtube.com/channel/UC9JuYJ_QR3ykzjXgnKIRviA" },
        { name: "LinkedIn", handle: "WhoPlays US", href: "https://www.linkedin.com/company/whoplays-us-inc/?viewAsMember=true" },
        { name: "X", handle: "@WhoPlaysUS", href: "https://x.com/WhoPlaysUS" },
        { name: "TikTok", handle: "@whoplays05", href: "https://tiktok.com/@whoplays05" },
      ],
    },
    workflow: {
      title: "Conéctate en cuatro pasos",
      description:
        "Descubre, organiza, invita y registra. WhoPlays se encarga de la coordinación para que pases más tiempo en la cancha.",
      steps: [
        {
          title: "Descubre",
          description:
            "Abre el feed de canchas para ver quién ya está jugando y qué niveles hay en pista.",
        },
        {
          title: "Crea",
          description:
            "Inicia un juego, ladder o entrenamiento en segundos con plantillas pensadas para canchas públicas.",
        },
        {
          title: "Invita",
          description:
            "Envía SMS o comparte el enlace; los jugadores responden Sí/No y WhoPlays actualiza la lista al instante.",
        },
        {
          title: "Registra",
          description:
            "Guarda resultados, sincroniza con DUPR si quieres y sigue el historial de tu comunidad.",
        },
      ],
    },
    testimonials: {
      title: "Organizadores que eligieron WhoPlays",
      items: [
        {
          quote:
            "“El feed de canchas cambió nuestra planificación. Veo quién ya confirmó y texto al resto sin salir de la app.”",
          name: "Lauren Mitchell",
          role: "Directora, Rally Point Pickleball",
        },
        {
          quote:
            "“Los RSVPs por SMS son un antes y un después. Incluso quienes odian las apps se mantienen informados y los cupos se llenan.”",
          name: "Brian Kim",
          role: "Head Pro, Marina Courts",
        },
      ],
    },
    finalCta: {
      title: "Ayúdanos a construir la próxima versión de WhoPlays",
      description:
        "Únete al grupo de organizadores fundadores para probar nuevas funciones de pickleball, pedir integraciones y recibir soporte directo mientras crecemos.",
      primaryCta: "Ser organizador fundador",
      primaryHref: "/founding-organizers",
      secondaryCta: "Descargar la app de pickleball",
      secondaryHref: "/app-stores",
    },
    footer: {
      tagline: "Hecho en EE. UU. para la comunidad global de pickleball.",
      rights: "Todos los derechos reservados.",
      links: {
        privacy: "Política de privacidad",
        plans: "Planes",
        accountDeletion: "Eliminar cuenta",
      },
    },
    subscriptionPlans: {
      hero: {
        eyebrow: "Precios flexibles",
        title: "Planes pensados para cada organizador",
        description:
          "Comienza gratis y escala a automatizaciones profesionales cuando tus programas crezcan. Cambia de nivel en cualquier momento.",
        note: "Todos los planes incluyen recordatorios de eventos, insights de asistencia y seguimiento de participación.",
      },
      cards: [
        {
          id: "free",
          name: "Free",
          badge: "Empieza aquí",
          priceMonthly: "Gratis / mes",
          priceYearly: "Gratis / año",
          description: "Ideal para probar los flujos de programación básicos con grupos pequeños y confirmaciones manuales.",
          features: [
            "Programaciones ilimitadas sin invitaciones",
            "Invita hasta 5 jugadores por evento",
            "15 invitaciones SMS al mes",
            "Insights del calendario y notificaciones",
          ],
          ctaLabel: "Crear cuenta gratis",
        },
        {
          id: "basic",
          name: "Scheduling Basic",
          badge: "Más popular",
          priceMonthly: "$2.99 / mes",
          priceYearly: "$24.99 / año",
          description: "Gestiona drop-ins semanales con listas de invitación más grandes y recordatorios SMS automáticos.",
          features: [
            "Hasta 5 calendarios activos con invitaciones",
            "Invita hasta 20 jugadores por evento",
            "75 invitaciones SMS al mes",
            "Experiencia sin anuncios y con tu marca",
          ],
          ctaLabel: "Elegir Basic",
          highlighted: true,
        },
        {
          id: "pro",
          name: "Scheduling Pro",
          badge: "Mejor valor",
          priceMonthly: "$4.99 / mes",
          priceYearly: "$39.99 / año",
          description: "Desbloquea invitaciones ilimitadas, prioridad en SMS y escala tus programas sin límites.",
          features: [
            "Calendarios ilimitados con invitaciones",
            "Jugadores ilimitados por evento",
            "150 invitaciones SMS al mes",
            "Entrega prioritaria de SMS para confirmar más rápido",
          ],
          ctaLabel: "Mejorar a Pro",
        },
      ],
      table: {
        title: "Compara lo que incluye cada plan",
        description:
          "Los planes mantienen desbloqueadas las herramientas principales. Actualiza cuando necesites más invitaciones o confirmaciones más rápidas.",
        featureColumnLabel: "Función",
        planLabels: {
          free: "Free",
          basic: "Scheduling Basic",
          pro: "Scheduling Pro",
        },
        rows: [
          {
            label: "Máx. calendarios activos sin invitaciones",
            values: { free: "Ilimitados", basic: "Ilimitados", pro: "Ilimitados" },
          },
          {
            label: "Máx. calendarios activos con invitaciones",
            values: { free: "1", basic: "5", pro: "Ilimitados" },
          },
          {
            label: "Máx. jugadores invitados por calendario",
            values: { free: "5", basic: "20", pro: "Ilimitados" },
          },
          {
            label: "Máx. canchas por evento",
            values: { free: "1", basic: "1", pro: "1" },
          },
          {
            label: "Máx. invitaciones SMS por mes",
            values: { free: "15", basic: "75", pro: "150" },
          },
          {
            label: "Insights de asistencia (¿quién se suma?)",
            values: { free: "check", basic: "check", pro: "check" },
          },
          {
            label: "Recordatorios y notificaciones",
            values: { free: "check", basic: "check", pro: "check" },
          },
          {
            label: "Experiencia sin anuncios",
            values: { free: "cross", basic: "check", pro: "check" },
          },
          {
            label: "Ventana de programación (días a futuro)",
            values: { free: "30", basic: "30", pro: "30" },
          },
          {
            label: "El organizador puede quedar fuera del roster",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. eventos de round robin al mes",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. jugadores por round robin",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. torneos de eliminación simple/doble al mes",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. jugadores por torneo de eliminación",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Entrega prioritaria de SMS",
            values: { free: "cross", basic: "check", pro: "check" },
          },
          {
            label: "Precio mensual",
            values: { free: "Gratis", basic: "$2.99", pro: "$4.99" },
          },
          {
            label: "Precio anual",
            values: { free: "Gratis", basic: "$24.99", pro: "$39.99" },
          },
        ],
        legend: {
          check: "Incluido",
          cross: "No incluido",
        },
      },
      cta: {
        title: "¿Necesitas acceso para equipo o torneos?",
        description:
          "Los formatos competitivos llegarán pronto. Súmate como organizador fundador para influir en la hoja de ruta y asegurar precios tempranos.",
        primaryLabel: "Habla con nosotros",
        primaryHref: "/founding-organizers",
        secondaryLabel: "Ver novedades del producto",
        secondaryHref: "/bugtracker",
      },
      footnote: "Gestiona upgrades directamente en la app móvil de WhoPlays. Precios expresados en USD.",
    },
    foundingOrganizers: {
      hero: {
        eyebrow: "Programa de organizadores fundadores",
        title: "Gestiona tu liga de pickleball mejor y gratis de por vida",
        highlight: "Los primeros 100 organizadores obtienen acceso de por vida a WhoPlays US.",
        description:
          "Organiza eventos, automatiza confirmaciones y ayuda a definir las próximas funciones. Mantén tus grupos activos y te daremos Scheduler Pro de por vida cuando lancemos las suscripciones.",
        badges: [
          {
            id: "google-play",
            label: "Disponible en Google Play",
            href: "https://play.google.com/store/apps/details?id=com.whosplays.app&pli=1",
            image: "/images/google-play-badge.png",
            width: 209,
            height: 63,
            className: "h-18 w-auto",
          },
          {
            id: "app-store",
            label: "Descargar en App Store",
            href: "https://apps.apple.com/app/apple-store/id6451442623?pt=126911496&ct=Founding%20Organizers&mt=8",
            image: "/images/apple-store-badge.svg",
            width: 175,
            height: 60,
            className: "h-12 w-auto",
          },
        ],
        primaryCtaLabel: "Programa tu primer evento",
          primaryCtaHref: "/app-stores",
      },
      lifetime: {
        title: "Scheduler Pro de por vida para organizadores activos",
        description:
          "Cuando lancemos las suscripciones, otorgaremos acceso Pro vitalicio a los organizadores que se mantengan activos.",
        bullets: [
          "Usa WhoPlays para programar y gestionar tus sesiones.",
          "Mantén al menos un evento cada 60 días.",
          "Haz crecer a tu comunidad y mantendremos Pro desbloqueado.",
        ],
        footnote: "Es nuestra forma de agradecer a quienes impulsan el crecimiento del pickleball desde el primer día.",
      },
      benefits: {
        title: "Beneficios clave",
        items: [
          "Programa partidos en segundos",
          "Invita a jugadores por SMS — sin app",
          "Controla cupos y confirma asistencias",
          "Visualiza quién juega por nivel y horario",
          "Gestiona tus eventos como un profesional",
        ],
      },
      reminder: {
        headline: "Solo 100 organizadores obtienen acceso vitalicio gratuito",
        subheadline: "Asegura tu lugar antes de que se agoten.",
      },
      video: {
        title: "Mira la programación de WhoPlays en acción",
        src: "https://whoplays.us/wp-content/uploads/2025/03/WhoPlays-Scheduler-Web-Version.mp4",
        poster: "https://whoplays.us/wp-content/uploads/2025/03/Image.png",
      },
    },
    privacyPolicy: {
      title: "Política de privacidad",
      lastUpdated: "Última actualización: 30 de marzo de 2025",
      intro: [
        "Esta Política de Privacidad describe nuestras políticas y procedimientos sobre la recopilación, el uso y la divulgación de su información cuando utiliza el Servicio, y explica sus derechos de privacidad y cómo la ley lo protege.",
        "Utilizamos sus datos personales para prestar y mejorar el Servicio. Al utilizar el Servicio, usted acepta la recopilación y el uso de la información de acuerdo con esta Política de Privacidad.",
      ],
      sections: [
        {
          heading: "Interpretación y definiciones",
          subSections: [
            {
              subheading: "Interpretación",
              paragraphs: [
                "Las palabras con iniciales en mayúscula tienen los significados definidos en las siguientes condiciones. Las definiciones siguientes tendrán el mismo significado, independientemente de que aparezcan en singular o en plural.",
              ],
            },
            {
              subheading: "Definiciones",
              paragraphs: ["Para los fines de esta Política de Privacidad:"],
              definitions: [
                {
                  term: "Cuenta",
                  description: "Significa una cuenta única creada para que usted acceda a nuestro Servicio o a partes de nuestro Servicio.",
                },
                {
                  term: "Afiliada",
                  description:
                    "Significa una entidad que controla, es controlada por o está bajo control común con una parte.",
                },
                {
                  term: "Aplicación",
                  description:
                    "Se refiere a WhoPlays US – Pickleball Scheduler & Tracker, el software proporcionado por la Compañía.",
                },
                {
                  term: "Compañía",
                  description:
                    '("la Compañía", "Nosotros" o "Nuestro") se refiere a WhoPlays US, Inc, 45 Whistler Trace, Ponte Vedra FL 32081.',
                },
                {
                  term: "País",
                  description: "Se refiere a: Florida, Estados Unidos.",
                },
                {
                  term: "Dispositivo",
                  description:
                    "Significa cualquier dispositivo que pueda acceder al Servicio, como una computadora, un teléfono móvil o una tableta digital.",
                },
                {
                  term: "Datos Personales",
                  description: "Es cualquier información que se relaciona con una persona identificada o identificable.",
                },
                { term: "Servicio", description: "Se refiere a la Aplicación." },
                {
                  term: "Proveedor de servicios",
                  description:
                    "Significa cualquier persona física o jurídica que procese los datos en nombre de la Compañía.",
                },
                {
                  term: "Datos de uso",
                  description:
                    "Se refiere a los datos recopilados automáticamente, ya sea generados por el uso del Servicio o por la propia infraestructura del Servicio.",
                },
                {
                  term: "Usted",
                  description: "Significa el individuo que accede o utiliza el Servicio.",
                },
              ],
            },
          ],
        },
        {
          heading: "Recopilación y uso de sus datos personales",
          subSections: [
            {
              subheading: "Datos personales",
              paragraphs: ["Podemos recopilar información personal como:"],
              bullets: [
                "Dirección de correo electrónico",
                "Nombre y apellido",
                "Número de teléfono",
                "Dirección, estado, provincia, código postal y ciudad",
              ],
            },
            {
              subheading: "Datos de uso",
              paragraphs: [
                "Esto incluye su dirección IP, el tipo y la versión del navegador, las páginas visitadas, la fecha y hora de la visita, el tiempo dedicado y otros datos de diagnóstico.",
              ],
            },
            {
              subheading: "Datos recopilados en la aplicación",
              paragraphs: ["Con su permiso, podemos recopilar información adicional:"],
              bullets: [
                "Datos de ubicación",
                "Contactos (para la función de invitaciones)",
                "Acceso a la cámara y biblioteca de fotos (para fotos de perfil o de eventos)",
              ],
              footnote: "Puede habilitar o deshabilitar el acceso a estos datos en cualquier momento desde la configuración de su dispositivo.",
            },
            {
              subheading: "Uso de sus datos personales",
              paragraphs: ["Utilizamos sus datos para los siguientes fines:"],
              bullets: [
                "Proporcionar y mantener nuestro Servicio",
                "Gestionar su cuenta",
                "Facilitar invitaciones por SMS y notificaciones de la aplicación",
                "Gestionar suscripciones y validar derechos de usuario",
                "Brindar soporte y responder a consultas",
                "Informarle sobre actualizaciones, ofertas y cambios",
                "Analizar el rendimiento y el uso para realizar mejoras",
              ],
            },
            {
              subheading: "Servicios de suscripción",
              paragraphs: [
                "WhoPlays US ofrece suscripciones gratuitas, básicas y profesionales, además de suscripciones específicas para torneos. Con fines promocionales, algunos usuarios pueden recibir acceso de por vida. Nos reservamos el derecho de revocar dicho acceso si el usuario no ha programado un evento ni utilizado funciones clave en un período de 60 días.",
                "Las suscripciones dentro de la aplicación pueden administrarse a través de Apple App Store o Google Play. Estas suscripciones siguen las políticas de facturación, cancelación y renovación definidas por esas plataformas.",
              ],
            },
          ],
        },
        {
          heading: "Conservación y eliminación de datos",
          paragraphs: [
            "Sus datos se conservan solo durante el tiempo necesario para fines legales, operativos y analíticos.",
            "Puede eliminar su cuenta o solicitar la eliminación de sus datos desde la aplicación o contactándonos.",
          ],
        },
        {
          heading: "Divulgación legal y seguridad",
          paragraphs: [
            "Podemos divulgar sus datos cuando la ley lo requiera, para proteger nuestros derechos o para garantizar la seguridad de los usuarios.",
            "Utilizamos medios comercialmente aceptables para proteger sus datos, pero no podemos garantizar una seguridad absoluta.",
          ],
        },
        {
          heading: "Privacidad de los menores",
          paragraphs: [
            "No recopilamos conscientemente información de personas menores de 13 años. Si cree que hemos recopilado datos de un menor, contáctenos.",
          ],
        },
        {
          heading: "Cambios en esta Política de Privacidad",
          paragraphs: [
            "Podemos actualizar esta Política de Privacidad. Si se realizan cambios materiales, se le notificará por correo electrónico o mediante un aviso en la aplicación.",
            "Todos los cambios entran en vigor cuando se publican.",
          ],
        },
      ],
      contact: {
        heading: "Contacto",
        paragraphs: ["Si tiene preguntas, comuníquese con nosotros a:"],
        entries: [{ label: "Correo electrónico", value: "support@whoplays.us" }],
      },
    },
    mySchedule: {
      back: "Volver a la app",
      title: "Mi agenda",
      create: "Crear evento",
      edit: "Editar",
      delete: "Eliminar",
      empty: "Sin partidos programados.",
      untitled: "Partido programado",
      startLabel: "Inicio",
      endLabel: "Fin",
      typeLabel: "Tipo",
      courtLabel: "Cancha",
      loading: "Cargando agenda...",
      refreshError: "No se pudo actualizar tu agenda.",
      cancel: "Cancelar",
      confirmCancel: "¿Cancelar este horario?",
    },
    myScheduleEditor: {
      titleCreate: "Programar evento",
      titleEdit: "Editar evento",
      subtitle: "Configura horario, cancha, formato e invitaciones.",
      encourage: "Invita amigos, incluso si no usan la app, para que tu partido sea más divertido.",
      dateLabel: "Fecha",
      startLabel: "Hora de inicio",
      endLabel: "Hora de fin",
      courtLabel: "Cancha",
      formatLabel: "Formato",
      invitesLabel: "Invitaciones",
      invitesCount: "Invitados: {count}",
      inviteCta: "Invitar jugadores ({count})",
      limitToggle: "Habilitar límite de confirmación",
      limitLabel: "Definir límite",
      limitHelper: "Comienza en 4 cuando habilitas el límite.",
      limitLocked: "El límite se habilita después de invitar a 4 jugadores.",
      saveCreate: "Guardar evento",
      saveEdit: "Guardar cambios",
      reset: "Reiniciar",
      validation: {
        dateRequired: "Selecciona una fecha.",
        courtRequired: "Selecciona una cancha.",
        endAfterStart: "La hora de fin debe ser después de la de inicio.",
        conflict: "Ya tienes un partido programado en este horario.",
        saveError: "No se pudo guardar. Intenta de nuevo.",
        saveSuccess: "Evento guardado correctamente.",
      },
      noCourt: "Necesitas una cancha primaria para programar un juego.",
    },
    myScheduleInvite: {
      title: "Enviar invitaciones",
      selected: "Seleccionados {count} invitados",
      back: "Volver a la agenda",
      addGuest: "Agregar invitado",
      playerNamePlaceholder: "Nombre del jugador",
      phonePlaceholder: "Número de teléfono",
      add: "Agregar",
      contactsUnavailable: "Agregar desde contactos (no disponible)",
      contactsAvailable: "Agregar desde contactos",
      myGroup: "Mi grupo",
      following: "Siguiendo",
      searchPlaceholder: "Buscar jugadores",
      noPlayers: "No se encontraron jugadores.",
      validation: {
        nameEmpty: "El nombre no puede estar vacío. Ingresa el nombre del invitado.",
        phoneEmpty: "El número de teléfono no puede estar vacío. Ingresa un teléfono válido.",
        phoneNumeric: "El número de teléfono solo puede contener dígitos.",
        phoneLength: "Número de EE. UU. inválido. Debe tener exactamente 10 dígitos.",
        phoneInvalid: "Número de teléfono inválido.",
        selfNumber: "Has agregado tu propio número. Ya estás incluido como anfitrión.",
        duplicateGuest: "Este jugador ya fue agregado como invitado en este horario.",
        registered: "Este jugador ya está registrado en WhoPlays. Agregamos su cuenta existente: {name}.",
      },
    },
  },
  pt: {
    languageName: "Português",
    nav: {
      features: "Recursos",
      workflow: "Como funciona",
      plans: "Planos",
      organizers: "Organizadores fundadores",
      login: "Entrar",
      signup: "Criar conta",
    },
    auth: {
      otp: {
        userNotFound: "Não encontramos uma conta com esse e-mail ou número de telefone.",
      },
    },
    toggles: {
      theme: "Tema",
      language: "Idioma",
      light: "Claro",
      dark: "Escuro",
      system: "Sistema",
    },
    hero: {
      eyebrow: "Jogadores e organizadores de pickleball conectados",
      title: "O app de pickleball que mantém suas quadras ativas",
      description:
        "WhoPlays é o app de pickleball para quadras públicas, clubes e programas municipais. Acompanhe a atividade em tempo real, envie convites por SMS e fortaleça sua comunidade de pickleball em um só lugar.",
      primaryCta: "Baixar o app de pickleball",
      primaryHref: "/app-stores",
      secondaryCta: "Ser organizador fundador",
      secondaryHref: "/founding-organizers",
      trust: "Confiado por comunidades de pickleball em quadras públicas e privadas.",
    },
    stats: [
      { value: "Feed de quadras", label: "Veja quando as quadras de pickleball estão lotadas em tempo real" },
      { value: "Convites SMS", label: "Preencha sessões de pickleball com convites por texto" },
      { value: "Estatísticas", label: "Registre partidas de pickleball e acompanhe seu rating DUPR" },
    ],
    appShell: {
      tabs: {
        realtime: "Ao vivo",
        network: "Minha rede",
        competitions: "Competições",
        matches: "Partidas",
        rank: "Ranking",
        messages: "MSG Center",
      },
      matches: {
        title: "Partidas",
        pending: "Pendentes",
        addMatch: "Adicionar partida",
        loadMore: "Carregar mais",
        loading: "Carregando partidas...",
        empty: "Nenhuma partida para mostrar.",
        segments: {
          owned: "Minhas",
          mygroup: "Meu grupo",
          mycourts: "Minhas sedes",
        },
        errors: {
          loadFailed: "Não foi possível carregar as partidas agora.",
          saveFailed: "Não foi possível salvar a partida.",
        },
        editor: {
          title: "Nova partida",
          subtitle: "Adicione placares e jogadores.",
          cancel: "Cancelar",
          save: "Salvar",
          saving: "Salvando...",
          format: "Formato",
          singles: "Simples",
          doubles: "Duplas",
          winner: "Vencedor",
          selectWinner: "Selecione o vencedor",
          team1: "Equipe 1",
          team2: "Equipe 2",
          player: "Usuário",
          score: "Placar",
        },
        validation: {
          missingPlayers: "Adicione todos os jogadores necessários.",
          missingWinner: "Selecione um vencedor.",
        },
      },
      rank: {
        title: "Ranking",
        loading: "Carregando rankings...",
        empty: "Sem dados de ranking para mostrar.",
        segments: {
          wprank: "WP Rank",
          overall: "Geral",
          h2h: "H2H",
        },
        filters: {
          skillAll: "Todos os níveis",
          singles: "Simples",
          doubles: "Duplas",
          week: "Semana",
          month: "Mês",
          all: "Todo período",
          allCourts: "Todas as quadras",
          groupNone: "Sem filtro de grupo",
          groupMyGroup: "Meu grupo",
          groupFollowing: "Seguindo",
          formatAll: "Todos os formatos",
          today: "Hoje",
          yesterday: "Ontem",
          lastWeek: "Semana passada",
          lastMonth: "Mês passado",
          threeMonths: "3 meses",
          sixMonths: "6 meses",
          year: "1 ano",
          fiveYears: "5 anos",
          allTime: "Todo período",
        },
        labels: {
          wins: "Vitórias",
          losses: "Derrotas",
          matches: "Partidas",
          winPct: "Win%",
          rankLabel: "Ranking",
        },
      },
      messagesTab: {
        title: "Central de mensagens",
        empty: "Nenhuma mensagem ainda.",
        clearAll: "Limpar tudo",
        markAllRead: "Marcar tudo como lido",
        delete: "Excluir",
        read: "Marcar lido",
        unread: "Marcar não lido",
        receivedAt: "Recebido",
      },
      socket: {
        schedule: "Agenda atualizada",
        match: "Atualização de partida",
        ping: "Novo ping recebido",
        dupr: "DUPR atualizado",
        generic: "Nova notificação",
        subscriptionTitle: "Assinatura atualizada",
        subscriptionBody: "Seu status de assinatura foi atualizado.",
        matchUnanimousTitle: "Partida aceita",
        matchUnanimousBody: "Boa notícia! Uma partida que você jogou foi aceita por todos. Agora é oficial!",
        matchAcceptedTitle: "Jogador aceitou a partida",
        matchAcceptedBody: "{player} aceitou a partida. Aguardando os demais.",
        matchPendingTitle: "Partidas pendentes",
        matchPendingBody: "Novos resultados pendentes! Confirme para contar no seu ranking.",
        duprUpdatedTitle: "DUPR atualizado",
        duprUpdatedBody: "Simples: {singles}\nDuplas: {doubles}",
        questionTitle: "Nova pergunta de agenda",
      },
      subscriptions: {
        title: "Assinaturas",
        subtitle: "Escolha um plano para destravar o agendamento no WhoPlays.",
        detailsLink: "Ver detalhes completos do plano",
        currentPlan: "Plano atual",
        statusLabel: "Status",
        free: "Grátis",
        active: "Ativo",
        select: "Selecionar",
        checkout: "Pagar com Stripe",
        close: "Fechar",
        manage: "Gerenciar / cancelar no Stripe",
        manageHint: "Abriremos o portal do Stripe. Se não vir sua assinatura, use o e-mail do recibo.",
        providerManaged: "Gerenciado por {provider}",
        providerManagedHint: "Para gerenciar ou cancelar esta assinatura, acesse {provider}.",
        error: "Não foi possível iniciar o checkout. Tente novamente.",
        successTitle: "Checkout concluído",
        successToast: "Checkout de assinatura concluído. Atualizando seu acesso…",
        cancelTitle: "Checkout cancelado",
        cancelToast: "Você cancelou o checkout no Stripe.",
        plans: {
          "com.whoplays.scheduling.basic.monthly": {
            label: "Scheduler Básico (Mensal)",
            price: "Cobrança mensal",
          },
          "com.whoplays.scheduling.basic.yearly": {
            label: "Scheduler Básico (Anual)",
            price: "Cobrança anual",
          },
          "com.whoplays.scheduling.pro.monthly": {
            label: "Scheduler Pro (Mensal)",
            price: "Cobrança mensal",
          },
          "com.whoplays.scheduling.pro.yearly": {
            label: "Scheduler Pro (Anual)",
            price: "Cobrança anual",
          },
        },
      },
      subscriptionLimits: {
        title: "Limite atingido",
        sms: "Você atingiu o limite mensal de {limit} convites por SMS.",
        players: "Você atingiu o limite de {limit} jogadores por agendamento.",
        events: "Você atingiu o limite de {limit} agendamentos ativos com convites.",
        generic: "Você atingiu o limite atual de {limit}.",
        upgrade: "Fazer upgrade",
        cancel: "Cancelar",
      },
      sidebar: {
        welcome: "Bem-vindo de volta",
        kpiWins: "Vitórias",
        kpiLosses: "Derrotas",
        kpiPending: "Pendentes",
        quickActions: {
          language: "Alterar idioma",
          languageNext: "Próximo: {language}",
          theme: "Alternar tema",
          tips: "Dicas (em breve)",
          tipsSoon: "As dicas estarão disponíveis em breve.",
          logout: "Sair",
        },
        toggles: {
          dnd: "Não perturbe",
          tips: "Dicas",
          share: "Compartilhar",
          language: "Idioma",
        },
        actions: {
          profile: "Perfil",
          preferredCourts: "Minhas sedes",
          schedule: "Minha agenda",
          linkDupr: "Conectar DUPR®",
          subscriptions: "Assinaturas",
          invite: "Convidar jogadores",
          privacy: "Política de privacidade",
          settings: "Configurações e preferências",
          appStats: "App Stats",
          logout: "Sair",
        },
        admin: {
          label: "Admin",
          access: "Acesso e permissões",
        },
        chartLabel: "Vitórias x derrotas",
        chartWins: "Vitórias",
        chartLosses: "Derrotas",
        chartPending: "Pendentes",
        chartNoData: "Ainda não há histórico de partidas.",
        chartWinRate: "Taxa de vitória",
        chartTotal: "Total",
        skillLevels: {
          beginner: "Iniciante",
          intermediate: "Intermediário",
          advanced: "Avançado",
        },
        rankings: {
          wpRankLabel: "Rank WP",
          duprLabel: "DUPR",
          singlesShort: "S",
          doublesShort: "D",
          notRated: "NR",
        },
      },
      header: {
        subtitleRank: "Ranking · Global",
        moreLabel: "Menu",
        currentLabel: "Atual",
      },
      analytics: {
        tabSwitch: "troca_de_aba",
        sidebarOpen: "sidebar_aberta",
        sidebarClose: "sidebar_fechada",
      },
      dupr: {
        title: "Conectar ao DUPR®",
        subtitle: "Entre no DUPR para sincronizar suas classificações.",
        instructions: "Conclua o login do DUPR abaixo. Não armazenamos sua senha.",
        waiting: "Aguardando login do DUPR...",
        processing: "Conectando sua conta DUPR...",
        success: "Conta DUPR conectada com sucesso.",
        error: "Não foi possível conectar sua conta DUPR agora.",
        originError: "Mensagem ignorada de uma origem não confiável.",
        missingConfig: "A integração DUPR não está configurada.",
        close: "Fechar",
        unauthorized: "Você precisa estar logado para conectar o DUPR. Faça login e tente novamente.",
      },
      realTime: {
        ribbonLabel: "Suas sedes",
        refresh: "Atualizar",
        noCourtsTitle: "Adicione uma sede preferida para ver a atividade ao vivo.",
        courtSelectorLabel: "Selecionar uma quadra",
        summary: {
          title: "Jogadores em {court}",
          allLevels: "Todos os níveis",
          advanced: "Avançado",
          intermediate: "Intermediário",
          beginner: "Iniciante",
          metrics: {
            willing: "Prontos para jogar",
            privateGroups: "Grupos privados",
            following: "Acompanhando",
          },
          metricsShort: {
            willing: "Prontos",
            privateGroups: "Privados",
            following: "Acomp.",
          },
          updated: "Atualizado {time}",
        },
        whosNextTitle: "Quem vem a seguir",
        myScheduleTitle: "Minha agenda",
        myQuestionsTitle: "Minhas perguntas",
        day: {
          today: "Hoje",
          tomorrow: "Amanhã",
        },
        metrics: {
          openPlay: "Open Play",
          event: "Evento",
          privateGroup: "Grupo privado",
        },
        carousels: {
          noData: "Sem atividade programada.",
          dotsLabel: "Ir para o cartão {index}",
        },
        detail: {
          noActivity: "Sem atividade agendada para este tipo.",
          close: "Fechar",
          profileTitle: "Perfil do jogador",
          openProfile: "Abrir perfil completo",
          unknownPlayer: "Jogador desconhecido",
          status: {
            host: "Anfitrião",
            accepted: "Aceito",
            pending: "Pendente",
            declined: "Recusado",
          },
        },
        schedule: {
          empty: "Sem partidas futuras.",
          badges: {
            invited: "Convidado",
            hosted: "Organizado por {name} em {court}",
            hostedSelf: "Organizado por mim em {court}",
          },
          cardTime: "Minha agenda: {time}",
          counts: {
            invited: "Convidados",
            confirmed: "Confirmados",
          },
          confirmedLine: "Confirmados: {confirmed}",
          confirmedLineWithLimit: "Confirmados: {confirmed} de {limit}",
        },
        questions: {
          empty: "Sem perguntas para esta sede.",
          status: {
            pending: "Pendente",
            answered: "Respondida",
          },
        },
        emptyHero: {
          title: "Marque uma partida e convide jogadores!",
          bullets: [
            "Veja quem está chegando — ao vivo!",
            "Convide por SMS e acompanhe as respostas",
            "Mantenha todos informados por SMS ou no app",
          ],
          cta: "Agendar evento",
        },
        fabLabel: "Agendar evento",
      },
      network: {
        title: "Minha Rede",
        subtitle: "Encontre e gerencie jogadores que você segue.",
        searchPlaceholder: "Buscar jogadores",
        search: "Buscar",
        segments: {
          suggestions: "Sugestões",
          following: "Seguindo",
          followers: "Seguidores",
        },
        courtFilter: "Quadras",
        allCourts: "Todas as quadras",
        loading: "Carregando jogadores…",
        error: "Não foi possível carregar os jogadores agora.",
        empty: "Nenhum jogador para mostrar.",
        unknownSkill: "Nível desconhecido",
        follow: "Seguir",
        following: "Seguindo",
        refresh: "Atualizar",
        loadMore: "Carregar mais",
        actionError: "Não foi possível atualizar o seguimento.",
        levelCompetition: {
          recreational: "Recreativo",
          competitive: "Competitivo",
        },
        followTitle: "Seguir {name}",
        followingTitle: "Seguindo {name}",
        close: "Fechar",
        toggleGroup: "Adicionar às estatísticas do Meu Grupo",
        toggleNotify: "Notificar-me sobre este jogador",
        unfollow: "Deixar de seguir",
        save: "Salvar",
      },
    },
    profileEdit: {
      loading: "Carregando perfil…",
      header: {
        title: "Editar perfil",
        back: "Voltar",
        cancel: "Cancelar",
        save: "Salvar preferências",
        saving: "Salvando…",
      },
      sections: {
        avatar: {
          title: "Avatar",
          subtitle: "Envie uma foto nítida para que os jogadores reconheçam você.",
          button: "Alterar foto",
          uploading: "Enviando…",
          helper: "JPG ou PNG até 5 MB.",
        },
        personal: { title: "Informações pessoais" },
        location: { title: "Localização" },
        preferences: { title: "Preferências e nível" },
        bio: { title: "Biografia", placeholder: "Compartilhe uma breve introdução" },
      },
      fields: {
        name: "Nome completo",
        nickname: "Apelido",
        email: "E-mail",
        phone: "Número de telefone",
        country: "País",
        state: "Estado",
        city: "Cidade",
        zipcode: "CEP",
        status: "Status",
        preferredLevel: "Nível preferido",
        matchType: "Tipo de partida",
        skill: "Nível de habilidade",
        selfRanking: "Auto classificação",
        competition: "Nível de competição",
        defaultAudience: "Audiência padrão",
        followerPrivacy: "Privacidade de seguidores",
      },
      helpers: {
        emailOptional: "E-mail é opcional e pode ser usado para notificações.",
        emailLogin: "Alterar seu e-mail pode exigir verificação.",
        phoneLocked: "Este número é seu login e não pode ser alterado.",
        phoneFormat: "Use o formato internacional, ex.: +55…",
      },
      success: {
        saved: "Perfil salvo.",
        photo: "Foto de perfil atualizada.",
        email: "E-mail verificado.",
        phone: "Telefone verificado.",
      },
      errors: {
        name: "O nome é obrigatório.",
        email: "Informe um e-mail válido.",
        phone: "Informe um telefone válido no formato internacional.",
        code: "Informe o código de 6 dígitos.",
        save: "Não foi possível salvar o perfil.",
        upload: "Não foi possível enviar o avatar.",
        verificationStart: "Não foi possível iniciar a verificação.",
        verificationConfirm: "Não foi possível validar o código.",
      },
      verification: {
        verifyEmail: "Verificar e-mail",
        reverifyEmail: "Reverificar e-mail",
        verifyPhone: "Verificar telefone",
        reverifyPhone: "Reverificar telefone",
        badge: "Verificado",
        modalTitle: "Informe o código de verificação",
        modalDescriptionEmail: "Enviamos um código de 6 dígitos para {target}. Digite-o para verificar seu e-mail.",
        modalDescriptionPhone: "Enviamos um código de 6 dígitos para {target}. Digite-o para verificar seu telefone.",
        submit: "Verificar",
        submitting: "Verificando…",
        cancel: "Cancelar",
      },
      options: {
        competition: [
          { value: 1, label: "Recreativo" },
          { value: 2, label: "Competitivo" },
        ],
      },
      legal: "WhoPlays US · Configurações de perfil",
    },
    myVenues: {
      back: "Voltar ao app",
      title: "Minhas sedes",
      subtitle: "Escolha sua sede principal e mantenha uma lista dos lugares onde gosta de jogar.",
      loading: "Carregando sedes…",
      primary: {
        heading: "Sede principal",
        empty: "Você ainda não selecionou uma sede principal.",
        required: "Selecione uma sede principal para personalizar sua experiência.",
        clear: "Remover sede principal",
      },
      preferred: {
        heading: "Sedes preferidas",
        empty: "Adicione as sedes onde joga com frequência para acessá-las rapidamente.",
        remove: "Remover",
        makePrimary: "Definir como principal",
        add: "Adicionar às preferidas",
        countLabel: "selecionadas",
      },
      badges: {
        primary: "Principal",
        preferred: "Preferida",
      },
      search: {
        heading: "Buscar sedes",
        placeholder: "Busque pelo nome da sede ou endereço",
        action: "Buscar",
        clear: "Limpar busca",
        useLocation: "Usar minha localização",
        locating: "Localizando…",
        locationReady: "Localização pronta",
        locationError: "Não foi possível obter sua localização.",
        prompt: "Busque uma sede ou compartilhe sua localização para ver opções próximas.",
        noResults: "Não encontramos sedes para essa busca. Experimente outros termos.",
        loadMore: "Carregar mais resultados",
        loading: "Carregando sedes…",
        setPrimary: "Definir como principal",
        addPreferred: "Adicionar às preferidas",
        activeLabel: "Resultados para “{query}”",
        error: "Não foi possível buscar sedes no momento.",
        viewDetails: "Ver detalhes",
        hideDetails: "Ocultar detalhes",
      },
      actions: {
        save: "Salvar sedes",
        saving: "Salvando sedes…",
        saved: "Sedes atualizadas com sucesso.",
        error: "Não foi possível salvar as sedes.",
        suggest: "Sugerir uma sede",
        reviewPending: "Rever sedes pendentes",
        warning: "As sedes foram salvas, mas não conseguimos atualizar os dados agora.",
      },
    },
    adminVenues: {
      back: "Voltar para Minhas sedes",
      title: "Sedes pendentes de verificação",
      subtitle: "Revise as sedes recém-enviadas antes de publicá-las.",
      loading: "Carregando sedes pendentes…",
      empty: "Não há sedes aguardando verificação.",
      countLabel: "Sedes pendentes: {count}",
      refresh: "Atualizar lista",
      error: "Não foi possível carregar as sedes pendentes.",
      reviewCta: "Abrir revisão",
      hoursHeading: "Horário de funcionamento",
      courtsHeading: "Configuração das quadras",
      totalCourts: "{count} quadras no total",
      amenitiesHeading: "Comodidades",
      openPlayHeading: "Sessões de open play hoje",
      pendingBadge: "Pendente de verificação",
    },
    adminAccess: {
      back: "Voltar ao painel",
      title: "Acesso e permissões",
      subtitle: "Conceda funções, funções por quadra e acesso a assinaturas.",
      loading: "Carregando dados de acesso…",
      error: "Não foi possível carregar o resumo administrativo.",
      refresh: "Atualizar",
      searchPlaceholder: "Buscar usuários ou sedes",
      usersEmpty: "Nenhum usuário corresponde aos filtros.",
      courtsEmpty: "Nenhuma sede corresponde aos filtros.",
      adminOnly: "Somente administradores.",
      unknownCourt: "Sede desconhecida",
      tabs: {
        users: "Usuários",
        courts: "Sedes",
      },
      filters: {
        all: "Todas",
        roles: "Funções",
        courtRoles: "Funções de sede",
        subscriptions: "Assinaturas",
      },
      roles: {
        heading: "Funções",
        empty: "Sem funções ainda.",
        add: "Adicionar função",
        select: "Selecione uma função",
      },
      courtRoles: {
        heading: "Funções de sede",
        empty: "Sem funções de sede.",
        add: "Adicionar função de sede",
        update: "Atualizar função",
        selectCourt: "Selecione uma sede",
        selectUser: "Selecione um usuário",
        roleLabel: "Função",
        searchPlaceholder: "Buscar sedes",
        searchUsersPlaceholder: "Buscar usuários",
      },
      access: {
        heading: "Acesso a assinaturas",
        empty: "Sem acessos concedidos.",
        add: "Adicionar acesso",
        update: "Atualizar acesso",
        selectPlan: "Selecione um plano",
        expiresAt: "Expira em",
      },
      actions: {
        remove: "Remover",
        edit: "Editar",
        close: "Fechar",
      },
      rules: {
        adminOnly: "Somente super admins podem adicionar ou remover a função admin.",
      },
    },
    adminStats: {
      back: "Voltar ao painel",
      title: "Estatisticas do app",
      subtitle: "Monitore atividade e crescimento.",
      loading: "Carregando estatisticas...",
      error: "Nao foi possivel carregar o resumo de estatisticas.",
      adminOnly: "Somente administradores.",
      periodLabel: "Periodo",
      controls: {
        period: "Periodo",
        timezone: "Fuso horario",
        topN: "Top N",
        pendingLimit: "Limite de pendentes",
        apply: "Aplicar",
        periodLabel: "Periodo selecionado",
      },
      sections: {
        lifetime: "Acumulado",
        period: "Periodo",
        activation: "Funil de ativacao",
        events: "Eventos",
        engagement: "Atividade",
        geo: "Geografia",
        pendingVenues: "Sedes pendentes",
        subscriptions: "Assinaturas",
        subscriptionsPeriod: "Assinaturas (Periodo)",
        downloadsByOs: "Downloads por sistema",
      },
      metrics: {
        uniqueUsers: "Usuarios",
        uniqueDownloads: "Downloads",
        profilesCreated: "Perfis criados",
        primaryCourtSelected: "Sede principal selecionada",
        sessionsCount: "Sessoes",
        eventsCount: "Eventos",
        sessionsDuration: "Duracao das sessoes",
        sessionsDurationLifetime: "Duracao acumulada",
      },
      empty: {
        downloads: "Sem downloads.",
      },
      funnel: {
        ofDownloads: "de downloads",
        ofPrevious: "do anterior",
      },
    },
    suggestVenue: {
      backToVenues: "Voltar para Minhas sedes",
      title: "Sugerir uma sede",
      subtitle: "Envie os dados de um novo local para análise da nossa equipe.",
      steps: {
        basic: "Básico",
        details: "Detalhes",
        openPlay: "Open play",
        photos: "Fotos",
        contact: "Contato",
        review: "Revisão",
      },
      actions: {
        previous: "Voltar",
        next: "Avançar",
        submit: "Enviar sugestão",
        submitting: "Enviando…",
        approve: "Aprovar e publicar",
        markPending: "Marcar como pendente",
        delete: "Excluir sede",
      },
      loadingExisting: "Carregando dados da sede…",
      successMessage: "Obrigado! Recebemos sua sugestão de sede.",
      standaloneSuccess: {
        title: "Obrigado por enviar uma nova sede!",
        body: "Nossa equipe revisará as informações e normalmente publica a sede em 24 a 72 horas após a validação.",
        helper: "Entraremos em contato se precisarmos de mais detalhes.",
        ctaPrimary: "Sugerir outra sede",
        ctaSecondary: "Fechar",
      },
      successApproved: "Sede aprovada e publicada.",
      successPending: "Marcada como pendente de ajustes.",
      successDeleted: "Sede excluída.",
      errors: {
        basicRequired: "Informe o nome e o endereço completo antes de continuar.",
        detailsRequired: "Adicione pelo menos uma configuração de quadra e escolha a superfície.",
        descriptionRequired: "Descreva a sede para que os jogadores saibam o que esperar.",
        contactRequired: "Precisamos dos seus dados de contato para um eventual retorno.",
        confirmInformation: "Confirme que as informações fornecidas estão corretas.",
        submitFailed: "Não foi possível salvar a sugestão. Tente novamente em instantes.",
        loadVenue: "Não foi possível carregar os dados da sede. Tente novamente.",
        deleteFailed: "Não foi possível excluir a sede. Tente novamente.",
      },
      confirmDelete: "Excluir esta sede? Essa ação não pode ser desfeita.",
      basic: {
        heading: "Informações básicas",
        subheading: "Nome e endereço nos ajudam a localizar a sede.",
        nameLabel: "Nome da sede",
        namePlaceholder: "Complexo Riverside Pickleball",
        shortNameLabel: "Nome curto",
        shortNamePlaceholder: "Etiqueta opcional",
        addressLabel: "Endereço",
        addressPlaceholder: "Rua Principal 123",
        cityLabel: "Cidade",
        stateLabel: "Estado / região",
        zipLabel: "CEP",
        countryLabel: "País",
        countryPlaceholder: "Brasil",
        latitudeLabel: "Latitude",
        longitudeLabel: "Longitude",
        mapHeading: "Prévia do mapa",
        mapHelper: "Informe latitude e longitude para visualizar o local.",
        mapMissing: "Adicione coordenadas para exibir o mapa.",
        mapTitle: "Prévia do mapa para {name}",
        mapDefaultName: "esta sede",
        mapLoadError: "Não foi possível carregar o mapa. Verifique sua conexão e tente novamente.",
        mapSearchingForward: "Buscando a localização para este endereço…",
        mapSearchingReverse: "Buscando o endereço para estas coordenadas…",
        mapGeocodeError: "Não encontramos coordenadas para esse endereço. Tente ajustá-lo.",
        mapPendingHeading: "Atualizar o endereço com esta localização?",
        mapPendingApply: "Usar este endereço",
        mapPendingIgnore: "Manter endereço atual",
      },
      details: {
        heading: "Detalhes da sede",
        subheading: "Informe estrutura das quadras, superfície e comodidades.",
        surfaceLabel: "Superfície principal",
        surfacePlaceholder: "Escolha a superfície",
        typeLabel: "Tipo de sede",
        typePublic: "Pública (aberta a todos)",
        typePrivate: "Privada (exige associação)",
        courtHeading: "Configuração das quadras",
        courtAdd: "Adicionar linha",
        courtTypeLabel: "Tipo de quadra",
        courtTypePlaceholder: "Selecione",
        courtTypeDedicated: "Dedicada",
        courtTypeShared: "Rede compartilhada",
        courtTypeLined: "Somente linhas",
        courtEnvironmentLabel: "Ambiente",
        courtEnvironmentPlaceholder: "Selecione o ambiente",
        environmentIndoor: "Coberta",
        environmentOutdoor: "Aberta",
        environmentCovered: "Coberta parcial",
        courtQuantityLabel: "Quantidade",
        courtRemove: "Remover linha",
        amenitiesHeading: "Comodidades",
        amenitiesOptions: [
          "Iluminação",
          "Banheiros",
          "Pontos de água",
          "Assentos",
          "Pro Shop",
          "Armários",
          "Chuveiros",
          "Alimentos/Bebidas",
          "Estacionamento",
          "Acessível",
          "Áreas com sombra",
          "Lançapetas",
          "Aulas disponíveis",
        ],
        hoursHeading: "Horário de funcionamento",
        timeSlots: [
          "5:00 AM",
          "6:00 AM",
          "7:00 AM",
          "8:00 AM",
          "9:00 AM",
          "10:00 AM",
          "11:00 AM",
          "12:00 PM",
          "1:00 PM",
          "2:00 PM",
          "3:00 PM",
          "4:00 PM",
          "5:00 PM",
          "6:00 PM",
          "7:00 PM",
          "8:00 PM",
          "9:00 PM",
          "10:00 PM",
          "11:00 PM",
          "12:00 AM",
        ],
        until: "até",
        closedLabel: "Fechado",
        descriptionLabel: "Descrição",
        descriptionPlaceholder: "Conte sobre a qualidade das quadras e avisos importantes.",
        phoneLabel: "Telefone",
        phonePlaceholder: "(Opcional) Número de contato",
        websiteLabel: "Site",
        websitePlaceholder: "https://…",
      },
      openPlay: {
        heading: "Agenda de open play",
        subheading: "Informe horários de jogo livre para outros jogadores encontrarem partidas.",
        sessionsCount: "{count} sessões",
        copyWeekdays: "Copiar para dias úteis",
        copyWeekend: "Copiar para fim de semana",
        addSession: "Adicionar sessão",
        emptyLabel: "Nenhuma sessão adicionada para este dia.",
        startLabel: "Início",
        endLabel: "Fim",
        levelLabel: "Nível",
        statusLabel: "Status",
        removeSession: "Remover sessão",
        timeSlots: [
          "6:00 AM",
          "7:00 AM",
          "8:00 AM",
          "9:00 AM",
          "10:00 AM",
          "11:00 AM",
          "12:00 PM",
          "1:00 PM",
          "2:00 PM",
          "3:00 PM",
          "4:00 PM",
          "5:00 PM",
          "6:00 PM",
          "7:00 PM",
          "8:00 PM",
          "9:00 PM",
          "10:00 PM",
        ],
        levelOptions: ["Todos os níveis", "Iniciante", "Iniciante+", "Intermediário", "Intermediário+", "Avançado", "Jogadores de torneio"],
        statusOptions: ["Disponível", "Poucas vagas", "Lotado", "Somente membros", "Aberto ao público", "Requer inscrição"],
      },
      photos: {
        heading: "Fotos da sede",
        subheading: "Adicione imagens para que os jogadores reconheçam o local.",
        helper: "Arraste e solte ou escolha arquivos do seu dispositivo.",
        uploadCta: "Selecionar fotos",
        supported: "Formatos JPG ou PNG (máx. 5 MB cada).",
        current: "Fotos existentes",
        pending: "Fotos para envio",
        remove: "Remover",
      },
      contact: {
        heading: "Informações de contato",
        subheading: "Informe quem podemos procurar caso seja necessário.",
        nameLabel: "Seu nome",
        emailLabel: "E-mail",
        phoneLabel: "Telefone",
        roleLabel: "Sua relação com a sede",
        rolePlaceholder: "Selecione o papel",
        roleOwner: "Proprietário / operador",
        roleManager: "Gerente",
        roleEmployee: "Funcionário",
        rolePlayer: "Jogador frequente",
        roleVisitor: "Visitante",
        roleOther: "Outro",
        notesLabel: "Observações",
        notesPlaceholder: "Compartilhe informações adicionais úteis para a análise.",
        confirmationLabel: "Confirmo que as informações estão corretas e seguem as diretrizes da comunidade.",
      },
      review: {
        heading: "Revisar e enviar",
        subheading: "Confira tudo antes de enviar para revisão.",
        basic: "Informações básicas",
        name: "Nome da sede",
        address: "Endereço",
        type: "Tipo de sede",
        surface: "Superfície",
        courts: "Quadras",
        amenities: "Comodidades",
        description: "Descrição",
        photos: "Fotos",
        contact: "Contato",
        contactName: "Nome",
        contactEmail: "E-mail",
        contactPhone: "Telefone",
        contactRole: "Papel",
        notes: "Notas",
        unknown: "Desconhecido",
      },
      duplicates: {
        title: "Esta sede talvez já exista",
        description: "Localizamos sedes parecidas. Se alguma coincidir, abra-a em vez de criar um duplicado.",
        view: "Ver sede",
        continue: "Continuar mesmo assim",
        cancel: "Voltar",
      },
    },
    settings: {
      header: {
        title: "Configurações e preferências",
        description: "Controle aparência, privacidade e notificações no WhoPlays.",
        back: "Voltar",
        cancel: "Cancelar",
        save: "Salvar preferências",
        saving: "Salvando…",
      },
      guard: {
        title: "Configurações indisponíveis",
        description: "Complete seu perfil para desbloquear as configurações.",
        cta: "Editar perfil",
      },
      theme: {
        title: "Preferência de tema",
        description: "Escolha como o WhoPlays aparece neste dispositivo.",
        options: {
          system: { label: "Sistema", description: "Usar configuração do dispositivo" },
          light: { label: "Claro", description: "Interface iluminada" },
          dark: { label: "Escuro", description: "Ideal para ambientes com pouca luz" },
        },
      },
      language: {
        title: "Idioma",
        description: "Altere o idioma da interface instantaneamente.",
      },
      availability: {
        title: "Disponibilidade",
        options: {
          1: { title: "Disponível", description: "Jogadores podem convidar você para partidas." },
          2: { title: "Indisponível", description: "Apareça offline para todos." },
          6: { title: "Grupo privado", description: "Visível apenas para membros de grupos privados." },
        },
      },
      visibility: {
        title: "Visibilidade do perfil",
        options: {
          1: { title: "Público", description: "Qualquer pessoa pode ver seu perfil." },
          3: { title: "Jogadores registrados", description: "Somente membros do WhoPlays podem ver seu perfil." },
          4: { title: "Conexões", description: "Apenas suas conexões veem todos os detalhes." },
          5: { title: "Privado", description: "Apenas você e administradores veem seu perfil." },
        },
      },
      followers: {
        title: "Visibilidade para seguidores",
        options: {
          1: { title: "Público", description: "Seguidores veem sua atividade pública." },
          2: { title: "Quadra principal", description: "Limite atualizações à comunidade da sua quadra." },
          3: { title: "Todas as quadras", description: "Seguidores veem atualizações de todas as quadras." },
          4: { title: "Amigos", description: "Somente amigos aprovados veem suas atualizações." },
        },
      },
      notifications: {
        title: "Notificações",
        description: "Escolha como deseja receber informações do WhoPlays.",
        items: {
          tournamentReminders: { title: "Lembretes de torneios", description: "Receba avisos antes dos torneios registrados." },
          matchNotifications: { title: "Atualizações de partidas", description: "Alertas quando partidas são agendadas ou alteradas." },
          partnerRequests: { title: "Pedidos de parceria", description: "Saiba quando quiserem se conectar com você." },
          communityEvents: { title: "Eventos da comunidade", description: "Fique por dentro de eventos na sua região." },
          emailNotifications: { title: "Notificações por e-mail", description: "Receba atualizações também por e-mail." },
          eventInvitesSms: { title: "Convites por SMS", description: "Receba convites por texto para responder rapidamente." },
          matchInvitesSms: { title: "Alertas de partidas por SMS", description: "Receba lembretes e detalhes via SMS." },
          scoringUpdatesSms: { title: "Pontuação por SMS", description: "Confirme resultados diretamente por texto." },
        },
      },
      account: {
        title: "Atalhos da conta",
        links: {
          connectedAccounts: { title: "Contas conectadas", description: "Gerencie redes sociais vinculadas." },
          dataPrivacy: { title: "Dados e privacidade", description: "Revise como tratamos seus dados." },
          deleteAccount: { title: "Excluir conta", description: "Remova sua conta permanentemente." },
        },
      },
      messages: {
        saved: "Preferências salvas com sucesso.",
        error: "Não foi possível salvar suas preferências. Tente novamente.",
      },
    },
    homeVenues: {
      title: "Locais perto de você",
      description:
        "Busque locais WhoPlays por nome ou endereço e acompanhe as sessões de open play de hoje.",
      pageTitle: "Diretório de locais de pickleball",
      pageDescription:
        "Pesquise por nome ou endereço, confira comodidades e veja sessões open play na sua região.",
      useMyLocation: "Usar minha localização",
      locating: "Detectando localização...",
      searchPlaceholder: "Pesquisar por nome ou endereço",
      searchAriaLabel: "Pesquisar locais",
      searchCta: "Buscar",
      promptLocation: "Compartilhe sua localização ou pesquise para encontrar locais próximos.",
      clearFilters: "Limpar filtros",
      errorLoading: "Não foi possível carregar os locais próximos. Tente novamente.",
      noResults: "Nenhum local corresponde à sua busca.",
      loading: "Carregando locais...",
      loadMore: "Carregar mais",
      loadingMore: "Carregando mais locais...",
      distanceAway: "A {distance}",
      verifiedBadge: "Verificado",
      courtsHeading: "Detalhe das quadras",
      courtsHeadingWithTotal: "Detalhe das quadras · {count} quadras",
      courtsFallback: "{count} quadras no total",
      surfaceLabel: "Superfície principal: {surface}",
      totalCourts: "{count} quadras no total",
      amenitiesHeading: "Comodidades",
      openPlayHeading: "Open play de hoje",
      hoursHeading: "Horário de funcionamento",
      hoursClosed: "Fechado",
      contactPhone: "Ligar para {phone}",
      visitWebsite: "Visitar site",
      viewOnMap: "Ver no mapa",
      viewAllVenues: "Ver todos os locais",
      viewDetails: "Ver detalhes",
      backToHome: "Voltar para a página inicial",
      openStatusNow: "Aberto agora",
      closedStatusNow: "Fechado agora",
      closedStatusToday: "Fechado hoje",
      openUntil: "Aberto até {time}",
      opensAt: "Abre às {time}",
      closedForDay: "Fechado pelo resto do dia",
      statusUnknown: "Status indisponível",
      statusClosedTemporary: "Fechado temporariamente",
      statusClosedPermanent: "Fechado permanentemente",
      statusClosedOutOfSeason: "Fechado fora da temporada",
      publicVenue: "Público",
      privateVenue: "Privado",
      mapFallbackTitle: "Mapa de {name}",
      mapFallbackNoLocation: "Sem informações de localização.",
    },
    features: {
      title: "Tudo o que sua comunidade de pickleball precisa",
      description:
        "Siga quadras, envie convites e registre partidas de pickleball com ferramentas criadas para quadras públicas.",
      cards: [
        {
          title: "Visão de quadras em tempo real",
          description:
            "Descubra o movimento antes de sair de casa e compartilhe o clima com quem segue a quadra.",
          points: [
            "Siga quadras públicas e veja quem joga por nível",
            "Confira sessões programadas e jogos abertos em um feed",
            "Envie atualizações rápidas para alertar quem acompanha a quadra",
          ],
        },
        {
          title: "Agendamento instantâneo e SMS",
          description:
            "Monte um jogo, ladder ou treino em segundos e convide por texto. Os jogadores respondem Sim/Não e o elenco se atualiza sozinho.",
          points: [
            "Modelos flexíveis para jogos abertos, ladders ou clínicas",
            "Convide por SMS sem obrigar ninguém a instalar o app",
            "Veja respostas na hora e envie lembretes automáticos",
          ],
        },
        {
          title: "Rede de jogadores conectada",
          description:
            "Registre confrontos, acompanhe o DUPR e crie relacionamentos dentro da comunidade.",
          points: [
            "Salve resultados, partidas verificadas e anotações pessoais",
            "Autoavalie seu nível ou sincronize com o DUPR",
            "Siga jogadores, abra chats e monte sua turma fixa",
          ],
        },
      ],
    },
    social: {
      title: "Siga a comunidade de pickleball da WhoPlays",
      description:
        "Dicas, novidades do produto e bastidores com os jogadores que estão construindo o app WhoPlays.",
      links: [
        { name: "Instagram", handle: "@whoplaysus", href: "https://www.instagram.com/whoplaysus/" },
        { name: "Facebook", handle: "WhoPlays Pickleball", href: "https://www.facebook.com/profile.php?id=61550796384597" },
        { name: "YouTube", handle: "WhoPlays Pickleball", href: "https://www.youtube.com/channel/UC9JuYJ_QR3ykzjXgnKIRviA" },
        { name: "LinkedIn", handle: "WhoPlays US", href: "https://www.linkedin.com/company/whoplays-us-inc/?viewAsMember=true" },
        { name: "X", handle: "@WhoPlaysUS", href: "https://x.com/WhoPlaysUS" },
        { name: "TikTok", handle: "@whoplays05", href: "https://tiktok.com/@whoplays05" },
      ],
    },
    workflow: {
      title: "Encontre jogos em quatro passos",
      description:
        "Descubra, crie, convide e registre. O WhoPlays cuida da coordenação para você aproveitar a quadra.",
      steps: [
        {
          title: "Descubra",
          description:
            "Abra o feed de quadras para ver quem já está jogando e qual é a mistura de níveis.",
        },
        {
          title: "Crie",
          description:
            "Monte uma sessão com modelos rápidos e convide por SMS; as respostas Sim/Não atualizam o elenco automaticamente.",
        },
        {
          title: "Confirme",
          description:
            "Acompanhe respostas em tempo real e envie lembretes para quem ainda não respondeu.",
        },
        {
          title: "Registre",
          description:
            "Anote resultados, sincronize com o DUPR se quiser e mantenha o histórico da comunidade.",
        },
      ],
    },
    testimonials: {
      title: "Organizadores que migraram para o WhoPlays",
      items: [
        {
          quote:
            "“O feed de quadras mudou nossa rotina. Eu vejo quem já confirmou e mando mensagem pro resto sem sair do app.”",
          name: "Lauren Mitchell",
          role: "Diretora, Rally Point Pickleball",
        },
        {
          quote:
            "“Os RSVPs por SMS são sensacionais. Mesmo quem não curte apps fica informado e as vagas não sobram.”",
          name: "Brian Kim",
          role: "Head Pro, Marina Courts",
        },
      ],
    },
    finalCta: {
      title: "Ajude a construir a próxima versão do WhoPlays",
      description:
        "Entre para o grupo de organizadores fundadores para testar novos recursos de pickleball, pedir integrações e receber suporte direto enquanto evoluímos.",
      primaryCta: "Ser organizador fundador",
      primaryHref: "/founding-organizers",
      secondaryCta: "Baixar o app de pickleball",
      secondaryHref: "/app-stores",
    },
    footer: {
      tagline: "Feito nos EUA para a comunidade global de pickleball.",
      rights: "Todos os direitos reservados.",
      links: {
        privacy: "Política de privacidade",
        plans: "Planos",
        accountDeletion: "Excluir conta",
      },
    },
    subscriptionPlans: {
      hero: {
        eyebrow: "Preços flexíveis",
        title: "Planos feitos para cada organizador",
        description:
          "Comece gratuitamente e evolua para automações profissionais quando seus programas crescerem. Troque de plano sempre que precisar.",
        note: "Todos os planos incluem lembretes de eventos, insights de presença e acompanhamento de participação.",
      },
      cards: [
        {
          id: "free",
          name: "Free",
          badge: "Comece aqui",
          priceMonthly: "Gratuito / mês",
          priceYearly: "Gratuito / ano",
          description: "Ideal para experimentar os fluxos de agendamento básicos com grupos pequenos e confirmações manuais.",
          features: [
            "Agendamentos ilimitados sem convites",
            "Convide até 5 jogadores por evento",
            "15 convites SMS por mês",
            "Insights do calendário e notificações",
          ],
          ctaLabel: "Criar conta grátis",
        },
        {
          id: "basic",
          name: "Scheduling Basic",
          badge: "Mais popular",
          priceMonthly: "$2.99 / mês",
          priceYearly: "$24.99 / ano",
          description: "Administre drop-ins semanais com listas de convite maiores e lembretes SMS automáticos.",
          features: [
            "Até 5 agendas ativas com convites",
            "Convide até 20 jogadores por evento",
            "75 convites SMS por mês",
            "Experiência sem anúncios com a sua marca",
          ],
          ctaLabel: "Escolher Basic",
          highlighted: true,
        },
        {
          id: "pro",
          name: "Scheduling Pro",
          badge: "Melhor custo-benefício",
          priceMonthly: "$4.99 / mês",
          priceYearly: "$39.99 / ano",
          description:
            "Desbloqueie convites ilimitados, entrega prioritária de SMS e escale seus programas sem limites.",
          features: [
            "Agendas ilimitadas com convites",
            "Jogadores ilimitados por evento",
            "150 convites SMS por mês",
            "Entrega prioritária de SMS para confirmações mais rápidas",
          ],
          ctaLabel: "Migrar para Pro",
        },
      ],
      table: {
        title: "Compare o que está incluído",
        description:
          "Todos os planos mantêm as ferramentas principais ativas. Faça upgrade quando precisar de mais convites ou confirmações mais rápidas.",
        featureColumnLabel: "Recurso",
        planLabels: {
          free: "Free",
          basic: "Scheduling Basic",
          pro: "Scheduling Pro",
        },
        rows: [
          {
            label: "Máx. agendas ativas sem convites",
            values: { free: "Ilimitadas", basic: "Ilimitadas", pro: "Ilimitadas" },
          },
          {
            label: "Máx. agendas ativas com convites",
            values: { free: "1", basic: "5", pro: "Ilimitadas" },
          },
          {
            label: "Máx. jogadores por agenda",
            values: { free: "5", basic: "20", pro: "Ilimitados" },
          },
          {
            label: "Máx. quadras por evento",
            values: { free: "1", basic: "1", pro: "1" },
          },
          {
            label: "Máx. convites SMS por mês",
            values: { free: "15", basic: "75", pro: "150" },
          },
          {
            label: "Insights de presença (quem confirmou?)",
            values: { free: "check", basic: "check", pro: "check" },
          },
          {
            label: "Lembretes e notificações",
            values: { free: "check", basic: "check", pro: "check" },
          },
          {
            label: "Experiência sem anúncios",
            values: { free: "cross", basic: "check", pro: "check" },
          },
          {
            label: "Janela de agendamento (dias antecipados)",
            values: { free: "30", basic: "30", pro: "30" },
          },
          {
            label: "Organizador fora da lista principal",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. eventos round robin por mês",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. jogadores por round robin",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. torneios eliminação simples/dúplice por mês",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. jogadores por torneio",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Entrega prioritária de SMS",
            values: { free: "cross", basic: "check", pro: "check" },
          },
          {
            label: "Preço mensal",
            values: { free: "Gratuito", basic: "$2.99", pro: "$4.99" },
          },
          {
            label: "Preço anual",
            values: { free: "Gratuito", basic: "$24.99", pro: "$39.99" },
          },
        ],
        legend: {
          check: "Incluído",
          cross: "Não incluído",
        },
      },
      cta: {
        title: "Precisa de acesso para equipe ou torneios?",
        description:
          "Os formatos competitivos chegam em breve. Entre como organizador fundador para influenciar o roadmap e garantir preços antecipados.",
        primaryLabel: "Fale conosco",
        primaryHref: "/founding-organizers",
        secondaryLabel: "Ver atualizações do produto",
        secondaryHref: "/bugtracker",
      },
      footnote: "Gerencie upgrades diretamente no app móvel do WhoPlays. Valores em USD.",
    },
    foundingOrganizers: {
      hero: {
        eyebrow: "Programa de organizadores fundadores",
        title: "Administre sua liga de pickleball melhor — e grátis para sempre",
        highlight: "Os primeiros 100 organizadores ganham acesso vitalício ao WhoPlays US.",
        description:
          "Organize eventos, automatize confirmações e ajude a definir os próximos recursos. Mantenha seus grupos ativos e liberaremos o Scheduler Pro vitalício quando as assinaturas forem lançadas.",
        badges: [
          {
            id: "google-play",
            label: "Disponível no Google Play",
            href: "https://play.google.com/store/apps/details?id=com.whosplays.app&pli=1",
            image: "/images/google-play-badge.png",
            width: 209,
            height: 63,
            className: "h-18 w-auto",
          },
          {
            id: "app-store",
            label: "Baixar na App Store",
            href: "https://apps.apple.com/app/apple-store/id6451442623?pt=126911496&ct=Founding%20Organizers&mt=8",
            image: "/images/apple-store-badge.svg",
            width: 175,
            height: 60,
            className: "h-12 w-auto",
          },
        ],
        primaryCtaLabel: "Agende seu primeiro evento",
          primaryCtaHref: "/app-stores",
      },
      lifetime: {
        title: "Scheduler Pro vitalício para organizadores ativos",
        description:
          "Quando as assinaturas estiverem no ar, concederemos acesso Pro vitalício aos organizadores que permanecerem engajados.",
        bullets: [
          "Use o WhoPlays para agendar e conduzir seus eventos.",
          "Realize pelo menos um evento a cada 60 dias.",
          "Mantenha sua comunidade crescendo e manteremos o Pro liberado.",
        ],
        footnote: "É a nossa forma de agradecer aos líderes que constroem o futuro do pickleball conosco.",
      },
      benefits: {
        title: "Benefícios principais",
        items: [
          "Agende partidas em segundos",
          "Convide jogadores por SMS — sem precisar de app",
          "Limite vagas e acompanhe confirmações",
          "Veja quem joga por nível e horário",
          "Gerencie seus eventos como um profissional",
        ],
      },
      reminder: {
        headline: "Apenas 100 organizadores ganham acesso vitalício gratuito",
        subheadline: "Garanta sua vaga antes que acabe.",
      },
      video: {
        title: "Veja o agendamento do WhoPlays em ação",
        src: "https://whoplays.us/wp-content/uploads/2025/03/WhoPlays-Scheduler-Web-Version.mp4",
        poster: "https://whoplays.us/wp-content/uploads/2025/03/Image.png",
      },
    },
    privacyPolicy: {
      title: "Política de Privacidade",
      lastUpdated: "Última atualização: 30 de março de 2025",
      intro: [
        "Esta Política de Privacidade descreve nossas políticas e procedimentos sobre a coleta, o uso e a divulgação das suas informações ao utilizar o Serviço, e explica seus direitos de privacidade e como a lei o protege.",
        "Usamos seus dados pessoais para fornecer e aprimorar o Serviço. Ao utilizar o Serviço, você concorda com a coleta e o uso das informações de acordo com esta Política de Privacidade.",
      ],
      sections: [
        {
          heading: "Interpretação e definições",
          subSections: [
            {
              subheading: "Interpretação",
              paragraphs: [
                "As palavras com iniciais em maiúscula têm significados definidos nas condições abaixo. As seguintes definições têm o mesmo significado, independentemente de aparecerem no singular ou no plural.",
              ],
            },
            {
              subheading: "Definições",
              paragraphs: ["Para os fins desta Política de Privacidade:"],
              definitions: [
                {
                  term: "Conta",
                  description: "Significa uma conta exclusiva criada para que você acesse nosso Serviço ou partes dele.",
                },
                {
                  term: "Afiliada",
                  description:
                    "Significa uma entidade que controla, é controlada por ou está sob controle comum com uma parte.",
                },
                {
                  term: "Aplicativo",
                  description:
                    "Refere-se ao WhoPlays US – Pickleball Scheduler & Tracker, o software fornecido pela Empresa.",
                },
                {
                  term: "Empresa",
                  description:
                    '("a Empresa", "Nós" ou "Nosso") refere-se a WhoPlays US, Inc, 45 Whistler Trace, Ponte Vedra FL 32081.',
                },
                {
                  term: "País",
                  description: "Refere-se a: Flórida, Estados Unidos.",
                },
                {
                  term: "Dispositivo",
                  description:
                    "Significa qualquer dispositivo que possa acessar o Serviço, como um computador, um celular ou um tablet.",
                },
                {
                  term: "Dados Pessoais",
                  description: "São todas as informações relacionadas a uma pessoa identificada ou identificável.",
                },
                { term: "Serviço", description: "Refere-se ao Aplicativo." },
                {
                  term: "Fornecedor de serviços",
                  description:
                    "Significa qualquer pessoa física ou jurídica que processe dados em nome da Empresa.",
                },
                {
                  term: "Dados de Uso",
                  description:
                    "Referem-se aos dados coletados automaticamente, gerados pelo uso do Serviço ou pela própria infraestrutura do Serviço.",
                },
                {
                  term: "Você",
                  description: "Significa o indivíduo que acessa ou utiliza o Serviço.",
                },
              ],
            },
          ],
        },
        {
          heading: "Coleta e uso dos seus dados pessoais",
          subSections: [
            {
              subheading: "Dados pessoais",
              paragraphs: ["Podemos coletar informações pessoais como:"],
              bullets: [
                "Endereço de e-mail",
                "Nome e sobrenome",
                "Número de telefone",
                "Endereço, estado, província, CEP/código postal e cidade",
              ],
            },
            {
              subheading: "Dados de uso",
              paragraphs: [
                "Isso inclui seu endereço IP, tipo e versão do navegador, páginas visitadas, data e hora da visita, tempo gasto e outros dados de diagnóstico.",
              ],
            },
            {
              subheading: "Dados coletados pelo aplicativo",
              paragraphs: ["Com sua permissão, podemos coletar informações adicionais:"],
              bullets: [
                "Dados de localização",
                "Contatos (para a função de convites)",
                "Acesso à câmera e à galeria de fotos (para fotos de perfil ou de eventos)",
              ],
              footnote: "Você pode ativar ou desativar o acesso a esses dados a qualquer momento nas configurações do dispositivo.",
            },
            {
              subheading: "Uso dos seus dados pessoais",
              paragraphs: ["Utilizamos seus dados para as seguintes finalidades:"],
              bullets: [
                "Fornecer e manter nosso Serviço",
                "Gerenciar sua conta",
                "Facilitar convites por SMS e notificações do aplicativo",
                "Gerenciar assinaturas e validar direitos de uso",
                "Oferecer suporte e responder a solicitações",
                "Informar sobre atualizações, ofertas e mudanças",
                "Analisar desempenho e uso para melhorias",
              ],
            },
            {
              subheading: "Serviços de assinatura",
              paragraphs: [
                "WhoPlays US oferece assinaturas gratuitas, básicas e profissionais, além de assinaturas específicas para torneios. Para fins promocionais, alguns usuários podem receber acesso vitalício. Reservamo-nos o direito de revogar esse acesso se o usuário não tiver agendado um evento ou utilizado recursos essenciais em um período de 60 dias.",
                "As assinaturas feitas pelo aplicativo podem ser gerenciadas pela Apple App Store ou pelo Google Play. Essas assinaturas seguem as políticas de cobrança, cancelamento e renovação definidas por essas plataformas.",
              ],
            },
          ],
        },
        {
          heading: "Retenção e exclusão de dados",
          paragraphs: [
            "Seus dados são mantidos apenas pelo tempo necessário para fins legais, operacionais e analíticos.",
            "Você pode excluir sua conta ou solicitar a exclusão dos seus dados pelo aplicativo ou entrando em contato conosco.",
          ],
        },
        {
          heading: "Divulgação legal e segurança",
          paragraphs: [
            "Podemos divulgar seus dados quando exigido por lei, para proteger nossos direitos ou para garantir a segurança dos usuários.",
            "Utilizamos meios comercialmente aceitáveis para proteger seus dados, mas não podemos garantir segurança absoluta.",
          ],
        },
        {
          heading: "Privacidade de crianças",
          paragraphs: [
            "Não coletamos intencionalmente informações de menores de 13 anos. Se você acredita que coletamos dados de uma criança, entre em contato conosco.",
          ],
        },
        {
          heading: "Alterações a esta Política de Privacidade",
          paragraphs: [
            "Podemos atualizar esta Política de Privacidade. Se mudanças materiais forem realizadas, você será notificado por e-mail ou por um aviso no aplicativo.",
            "Todas as alterações entram em vigor quando publicadas.",
          ],
        },
      ],
      contact: {
        heading: "Fale conosco",
        paragraphs: ["Se tiver dúvidas, entre em contato conosco:"],
        entries: [{ label: "E-mail", value: "support@whoplays.us" }],
      },
    },
    mySchedule: {
      back: "Voltar ao app",
      title: "Minha agenda",
      create: "Criar evento",
      edit: "Editar",
      delete: "Excluir",
      empty: "Sem partidas agendadas.",
      untitled: "Partida agendada",
      startLabel: "Início",
      endLabel: "Fim",
      typeLabel: "Tipo",
      courtLabel: "Quadra",
      loading: "Carregando agenda...",
      refreshError: "Não foi possível atualizar sua agenda.",
      cancel: "Cancelar",
      confirmCancel: "Cancelar este horário?",
    },
    myScheduleEditor: {
      titleCreate: "Agendar evento",
      titleEdit: "Editar evento",
      subtitle: "Defina horário, quadra, formato e convites.",
      encourage: "Convide amigos — mesmo sem app — para deixar o jogo mais divertido.",
      dateLabel: "Data",
      startLabel: "Hora de início",
      endLabel: "Hora de término",
      courtLabel: "Quadra",
      formatLabel: "Formato",
      invitesLabel: "Convites",
      invitesCount: "Convidados: {count}",
      inviteCta: "Convidar jogadores ({count})",
      limitToggle: "Habilitar limite de confirmação",
      limitLabel: "Definir limite",
      limitHelper: "Começa em 4 quando o limite é habilitado.",
      limitLocked: "O limite aparece após convidar 4 jogadores.",
      saveCreate: "Salvar evento",
      saveEdit: "Salvar alterações",
      reset: "Reiniciar",
      validation: {
        dateRequired: "Selecione uma data.",
        courtRequired: "Selecione uma quadra.",
        endAfterStart: "A hora de término deve ser depois da de início.",
        conflict: "Você já tem um jogo agendado nesse horário.",
        saveError: "Falha ao salvar. Tente novamente.",
        saveSuccess: "Evento salvo com sucesso.",
      },
      noCourt: "Você precisa de uma quadra primária para agendar um jogo.",
    },
    myScheduleInvite: {
      title: "Enviar convites",
      selected: "Selecionados {count} convidados",
      back: "Voltar à agenda",
      addGuest: "Adicionar convidado",
      playerNamePlaceholder: "Nome do jogador",
      phonePlaceholder: "Número de telefone",
      add: "Adicionar",
      contactsUnavailable: "Adicionar dos contatos (indisponível)",
      contactsAvailable: "Adicionar dos contatos",
      myGroup: "Meu grupo",
      following: "Seguindo",
      searchPlaceholder: "Buscar jogadores",
      noPlayers: "Nenhum jogador encontrado.",
      validation: {
        nameEmpty: "O nome não pode ficar vazio. Informe o nome do convidado.",
        phoneEmpty: "O número de telefone não pode ficar vazio. Informe um telefone válido.",
        phoneNumeric: "O telefone deve conter apenas dígitos.",
        phoneLength: "Número dos EUA inválido. Deve ter exatamente 10 dígitos.",
        phoneInvalid: "Número de telefone inválido.",
        selfNumber: "Você adicionou seu próprio número. Você já está incluído como anfitrião.",
        duplicateGuest: "Este jogador já foi adicionado como convidado neste horário.",
        registered: "Este jogador já está registrado no WhoPlays. Adicionamos a conta existente: {name}.",
      },
    },
  },
} as const;

export const AVAILABLE_LOCALES = Object.entries(messages).map(([value, config]) => ({
  value: value as Locale,
  label: config.languageName,
}));

export function isLocale(value: string | null | undefined): value is Locale {
  if (!value) return false;
  return value === "en" || value === "es" || value === "pt";
}

export type Translations = (typeof messages)[Locale];
